/** @jsxImportSource ./simboluri-jsx */
// EDITORUL SOLUȚIEI DE STRUCTURĂ — geometria macro, desenată de om (A8,
// contract §5.4).
//
// Se deschide pe orice rând de structură gata — Schița, geometria macro sau
// calculul de detaliu —, care rămâne SURSA și nu se schimbă: planul unui nivel
// cu pereții arhitecturii dedesubt, elementele verticale (ESV) și grinzile
// nivelului deasupra. Se poate:
//
//   * adăuga un ESV — clic pe plan; nodul cade pe axa zidului în care a căzut
//     și se aliniază cu nodurile altor ESV (`lipesteNodul`);
//   * muta un ESV — tras cu mâna, cu aceeași lipire, sau scriind pe desen
//     golul până la vecin / capătul zidului;
//   * redimensiona un ESV — trăgând de capătul liber al unei piese (piciorul,
//     sau brațul unui L/T, fiecare de capul lui), scriind pe desen latura
//     lungă sau grosimea, sau în panoul din dreapta; tras până în fața altui
//     ESV, se unește cu el, iar grinda dintre ele pleacă (`editorCapete`);
//   * adăuga o grindă — clic pe o piesă a unui ESV, apoi pe o piesă a altuia:
//     grinda se așază pe LINIA COMUNĂ a celor două piese — paralelă cu lamela
//     pe care o continuă (prinsă în vârful ei) sau perpendiculară pe ea
//     (intră prin fața lungă) —, dreaptă prin construcție, în orice ordine
//     ar veni clicurile (`capeteleGrinziiNoi`, `liniaAleasa`); cu Alt capătul
//     se prinde unde dă omul clic, lipit de punctele de ancorare ale
//     elementului (`ancorareaPeEsv`); o grindă oblică se face și rămâne
//     semnalată, iar fără nicio linie comună — grinda stă cu TOATĂ lățimea
//     pe fiecare element, altfel nu e grindă — editorul spune cu cât lipsește
//     betonul (`golulDeLinie`); după ea unealta revine la «Selectează», cu
//     grinda aleasă, ca la ESV și la placă;
//   * șterge un ESV sau o grindă — pe nivelul ăsta (și, la ESV, în sus) sau pe
//     toate nivelurile;
//   * vedea placa de la capul nivelului, în ochiuri — ale FEM-ului, venite cu
//     verificarea (`editorPlaci`); ochiurile le fac grinzile, deci o grindă
//     trasă sau ștearsă le împarte sau le unește. Pe placă omul doar scoate
//     sau pune placa într-un ochi, acoperă un balcon rămas sub cer, desenează
//     o placă nouă și trage de conturul unei console; straturile (ochiuri,
//     ESV, grinzi, pereți, etichete) se sting din meniu.
//
// Nimic calculat nu se arată aici: costurile, încărcările și verificările
// sursei nu mai sunt ale geometriei desenate. Ciorna stă în interfață, per
// vizitator; serverul doar o VERIFICĂ, după o scurtă pauză
// (`verificaEditare`), fără să scrie nimic. «Salvează ca Soluția N+1» naște
// rândurile noi — geometria omului e macro-ul lor (D10) —, iar calculul de
// detaliu îl pornește omul. Renunțarea nu lasă nimic în urmă.
//
// Tipurile de ESV sunt LIBERE aici (D8): contorul «Tipuri ESV folosite: n»
// doar le arată.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useT, type Cheie } from "../i18n";
import { fitBbox, type View } from "../viewer/view";
import StoreyView from "../viewer/plan/StoreyView";
import { laOrigineLocala, origineDesen, toStoreyPlans } from "../viewer/canonical";
import { laLume, useGesturi } from "../viewer/tronson/Panza2D";
import { CotaDesen } from "../viewer/tronson/CoteDesen";
import { PAL } from "./paletaDesen";
import { indexParter } from "./elemente";
import {
  deschideEditor, salveazaCaSolutieNoua, verificaEditare, type EditorDeschis,
} from "./apiStructura";
import type { SolutieRand } from "../nav/api";
import { useRegistruOptional } from "../nav/Registru";
import { numeleEditarii } from "../nav/selectie";
import { tineNescrisul } from "../nescris";
import {
  adaugaEsv, adaugaGrinda, amprentaEsv, ancorareaPeEsv, ancorareaSubPunct, aseazaGrinzile, axelePeretilor,
  cadrulContextului, capeteleGrinziiNoi,
  capatulGrinzii, conturulTronsonului, coteleEsv, coteleTronsonului, cutiaEsv, deplasareaCotei, esvSubPunct,
  golulDeLinie, greseli, grindaIntre, grindaSubPunct, imparteGrinda,
  esvSchimbate, ghidajeleAxelor, iesirileDeDesenat, laEcran, laPlansa, LATURA_MIN_CM, liniileIntre, lipesteNodul,
  mutaEsv, nivelurileEsv,
  nivelurileGrinzii, panaLaNivel, piesaSubPunct, puncteDeAncorare, redimEsv, redimGrinda,
  reancoreazaCapatul, rotesteEsv, sectiunea, sectiuneaObisnuita, stergeEsv, stergeGrinda,
  type Ancorare, type AxaArh, type AxaPerete, type Cadru, type CapatDePlecare, type CotaEsv, type Geometrie,
  type Iesire, type Lipire, type Pt,
  type SectiuneGrinda, type VerdictAmplasare,
} from "./editorGeometrie";
import {
  aplicaContinuarea, betonulContinuarii, continuari, numeleFormei, redimBrat, stergeBrat,
  type Continuare,
} from "./editorForme";
import {
  BRAT_MIN_MM, conturulEsv, lungimeaPiesei, piesele, trageCapatul, undeAjunge, unesteEsv,
  type Capat,
} from "./editorCapete";
import {
  adaugaColt, adaugaPlaca, ariaInel, cutie, diferentaContur, inPoligon, interior, lipesteColt, mutaColt,
  ochiuriVazute, peAlteNiveluri, placiNivel, punePlacaLaLoc, schimbaPlaca, scoateColt,
  scoatePlaca, semnaleleOchiului, simplificaInel, suprapunere, zoneleSchimbate,
  type ConsolaPlaca, type OchiPlaca, type OchiVazut, type PlacaNivel,
} from "./editorPlaci";
import { cheiaEsv, cheiaGrinzii, clasele, indexBratului, membrii, peMembri, urmeazaClasa, type Clasa } from "./editorClase";
import { geamanii, gemeniiDeUrmat, grupurileLui, urmeazaSimetria, type GrupSimetrie, type Operatie } from "./editorSimetrie";
import HoverCard from "./HoverCard";
import {
  citesteClipboardul, copie, lipesteEsv, lipesteProprietati, numeleCopiei, sePoateLipiPe,
  sectiuneaGrinziiCopiate, tineClipboardul, type Clipboard,
} from "./editorClipboard";
import { CALC_IMPLICIT, planseeleNivelului, transformarea, type Calc } from "./calcArhitectura";
import { useDocumentArhitectura } from "./documentArhitectura";
import { importulPlansei } from "./desenPlansa";
import { PlansaArhitect, useDesenPlansa } from "./PlansaArhitect";
import "./editorStructura.css";

type Unealta = "alege" | "esv" | "grinda" | "placa";
/** Pe ce niveluri lucrează o operație: nivelul de pe ecran, sau toate. */
type Domeniu = "nivel" | "toate";
/** Ochiul se ține minte prin punctul în care s-a dat clic, nu prin id: id-ul
 *  îl dă serverul după arie și se schimbă la fiecare grindă trasă. Golul e un
 *  inel scos de om din placă (`editorPlaci`). */
type Ales = { fel: "esv" | "grinda"; id: string } | { fel: "ochi"; p: Pt } | { fel: "gol"; inel: Pt[] }
  | null;

/** STRATURILE planului: ce e stins nu se desenează și nu se poate alege. */
type Straturi = { ochiuri: boolean; esv: boolean; grinzi: boolean; pereti: boolean; etichete: boolean;
                  zidarie: boolean };
const STRATURI_IMPLICITE: Straturi = { ochiuri: true, esv: true, grinzi: true, pereti: true, etichete: true,
                                       zidarie: true };
/** Straturile sunt ale vizitatorului, nu ale soluției: stau în browser. */
const CHEIE_STRATURI = "ob.editor-structura.straturi";
function straturileTinute(): Straturi {
  try {
    return { ...STRATURI_IMPLICITE, ...JSON.parse(localStorage.getItem(CHEIE_STRATURI) ?? "{}") };
  } catch {
    return STRATURI_IMPLICITE;
  }
}
/** Câte nuanțe au ochiurile: vecinii primesc nuanțe diferite (`nuanteleOchiurilor`). */
const NUANTE_OCHI = 5;
/** Cât de mult se suprapune «același» ochi sau «aceeași» consolă pe alt
 *  nivel, ca o schimbare pe «Toate nivelurile» să meargă și acolo. */
const ACELASI_OCHI = 0.9;
const ACEEASI_CONSOLA = 0.8;
const ACELASI_GOL = 0.5;

/** Tușul și corpul cotelor de lucru: negre și un pic mai mari decât cele de
 *  planșă, ca să se citească peste betonul albastru. */
const COTA_NEGRU = "#141414";

/** Ce se spune despre ieșirea unui ESV: prin gol (ușă, fereastră) — betonul
 *  trece prin gol, nu iese din zid —, în afara zidurilor, sau cât iese. */
function textulIesirii(t: ReturnType<typeof useT>, id: string, i: Iesire): string {
  if (i.gol) return t("struct.editor.iesire_gol", { id, gol: i.gol.id ?? "", tip: i.gol.tip ?? "gol" });
  // verdictul solverului: betonul în cameră, cu aria pe care o numără el
  if (i.sursa === "solver") return t("struct.editor.iesire_camera", { id, cm2: Math.round(i.arie_cm2 ?? 0) });
  if (i.afara) return t("struct.editor.iesire_afara", { id });
  return t("struct.editor.iesire_cm", { id, cm: (i.adancime_mm / 10).toFixed(1).replace(".", ",") });
}
const COTA_CORP = 14;
/** Tușul cotelor tronsonului: mai stins decât al cotelor de lucru, care se
 *  scriu — ele doar se citesc. */
const COTA_TRONSON = "#56606b";
/** Culoarea selecției — aur, nu e a niciunei familii (`PAL.selectat`). */
const SEL = "#c8961a";
/** Cât de aproape (px) se lipește fața trasă de un zid sau de un gol. */
const LIPIRE_FATA_PX = 8;
/** Sub atâția pixeli pe ecran o piesă nu primește mâner: tot ce e acolo e al
 *  mutării (se trage după ce se mărește planul, sau se scrie în panou). */
const PIESA_MIN_PX = 10;
/** Pauza după care o schimbare se trimite la verificat. */
const VERIFICARE_MS = 700;
/** Cât se poate anula. */
const ISTORIC_MAX = 100;

/** Mărirea editorului e practic liberă: de la tot proiectul pe un colț de
 *  ecran până la milimetru. */
const K_MIN_EDITOR = 0.05;
const K_MAX_EDITOR = 50_000;

type Props = {
  projectId: string;
  /** id-ul rândului SURSĂ (S0, M sau D, gata) */
  sursa: string;
  /** numai de citit: se vede geometria, nu se schimbă */
  doarCitire?: boolean;
  /** ce stă în capul meniului din stânga */
  sus?: ReactNode;
  /** corpul privit (`?corp=`): editorul se deschide pe el, dacă se poate */
  corp?: string | null;
  /** «Renunță»: ciorna se aruncă */
  laInchide: () => void;
  /** «Salvează ca Soluția N+1» a reușit */
  laSalvat: (r: { macro: SolutieRand }) => void;
};

/** Încărcarea: masa se montează abia cu geometria sosită, ca pânza pe care se
 *  leagă rotița și panoramarea (`useGesturi`) să existe de la prima randare. */
export default function EditorStructura(p: Props) {
  const t = useT();
  const [editare, setEditare] = useState<EditorDeschis | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  useEffect(() => {
    let viu = true;
    setEditare(null);
    setEroare(null);
    deschideEditor(p.projectId, p.sursa)
      .then((e) => { if (viu) setEditare(e); })
      .catch((err: Error) => viu && setEroare(err.message));
    return () => { viu = false; };
  }, [p.projectId, p.sursa]);
  if (eroare) return <div className="ob-studio-gol"><div><p>{eroare}</p></div></div>;
  if (!editare) {
    return <div className="ob-studio-gol"><div><p>{t("struct.editor.se_incarca")}</p></div></div>;
  }
  return <Masa {...p} editare={editare} />;
}

function Masa({
  projectId, sursa, doarCitire = false, sus, corp: corpPrivit, laInchide, laSalvat, editare,
}: Props & { editare: EditorDeschis }) {
  const t = useT();
  // pe corpul privit, dacă are geometrie; altfel pe primul care are — un corp
  // fără Pasul 4 nu se poate edita, dar nu-i oprește nici pe ceilalți
  const [corpIdx, setCorpIdx] = useState(() => {
    const bun = (i: number) => i >= 0 && !editare.corpuri[i]?.indisponibil;
    const privit = editare.corpuri.findIndex((c) => c.cod === corpPrivit);
    if (bun(privit)) return privit;
    return Math.max(0, editare.corpuri.findIndex((c) => !c.indisponibil));
  });
  // geometria fiecărui corp, cu istoricul ei
  const [geo, setGeo] = useState<Record<number, Geometrie>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.geometrie])));
  const trecut = useRef<Record<number, Geometrie[]>>({});
  const viitor = useRef<Record<number, Geometrie[]>>({});
  const [nivel, setNivel] = useState<number | null>(null);
  const [unealta, setUnealta] = useState<Unealta>("alege");
  const [domeniu, setDomeniu] = useState<Domeniu>("toate");
  const [ales, setAles] = useState<Ales>(null);
  /** cu unealta grindă: elementul primului clic și punctul (mm) în care
   *  grinda se prinde de el */
  /** unealta grindă: de unde pleacă grinda — elementul și piesa primului
   *  clic, sau punctul exact, când omul l-a fixat cu Alt */
  const [grindaDe, setGrindaDe] = useState<CapatDePlecare | null>(null);
  /** Alt apăsat: capătul grinzii se prinde exact unde îl lasă omul, nu pe
   *  linia comună a pieselor; punctele de ancorare se arată cât e apăsat */
  const [altApasat, setAltApasat] = useState(false);
  useEffect(() => {
    const alt = (e: KeyboardEvent) => { if (e.key === "Alt") setAltApasat(e.type === "keydown"); };
    const sus = () => setAltApasat(false);
    window.addEventListener("keydown", alt);
    window.addEventListener("keyup", alt);
    window.addEventListener("blur", sus);
    return () => {
      window.removeEventListener("keydown", alt);
      window.removeEventListener("keyup", alt);
      window.removeEventListener("blur", sus);
    };
  }, []);
  const [cursor, setCursor] = useState<Pt | null>(null);
  const [lipire, setLipire] = useState<Lipire | null>(null);
  /** «+»-ul de sub cursor: betonul lui se arată înainte de clic */
  const [plusPeste, setPlusPeste] = useState<number | null>(null);
  /** cu unealta ESV: grinda de sub cursor, pe care elementul ar împărți-o */
  const [grindaSub, setGrindaSub] = useState<string | null>(null);
  const [cotaScrisa, setCotaScrisa] = useState<number | null>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);
  // un mesaj e răspunsul la un gest (copiat, lipit, unit): se citește o dată
  // și pleacă singur, ca să nu stea texte pe desen
  useEffect(() => {
    if (!mesaj) return;
    const t = setTimeout(() => setMesaj(null), 5000);
    return () => clearTimeout(t);
  }, [mesaj]);
  // «verificat» = serverul a văzut ultima geometrie; «de_verificat» = s-a
  // schimbat ceva de atunci
  /** clasa (cheia ei, `editorClase`) aprinsă cu mouse-ul din listă sau din
   *  cipul panoului: membrii ei primesc conturul auriu pe plan */
  const [clasaAprinsa, setClasaAprinsa] = useState<string | null>(null);
  /** pe câte elemente se scrie secțiunea: pe cel ales, sau pe toată clasa
   *  lui (`editorClase`). Implicit pe element: cel schimbat iese din clasă.
   *  Mutarea, ștergerea, rotirea și nivelul rămân mereu pe element. */
  const [peClasa, setPeClasa] = useState(false);
  /** gemenii de simetrie (`editorSimetrie`) urmează mutarea și secțiunea,
   *  oglindite; omul poate stinge asta cât lucrează la un element singur */
  const [simetric, setSimetric] = useState(true);
  /** grupul de simetrie ales din panou (`grupurileLui`): toți membrii lui
   *  sunt selectați pe plan, cu elementul din panou printre ei; cel aprins
   *  cu mouse-ul de pe rândul lui se vede punctat */
  const [grupAles, setGrupAles] = useState<{ cheie: string; membri: string[] } | null>(null);
  const [grupAprins, setGrupAprins] = useState<string[] | null>(null);
  // grupul ține cât elementul din panou e unul dintre membri: un clic pe alt
  // element (sau Esc) îl lasă
  useEffect(() => {
    if (!grupAles) return;
    if (!ales || ales.fel !== "esv" || !grupAles.membri.includes(ales.id)) setGrupAles(null);
  }, [ales, grupAles]);
  /** «+» desfăcut: felurile de creat (ESV · Grindă · Placă) se văd; se
   *  închide când unealta se întoarce la selecție */
  const [creare, setCreare] = useState(false);
  /** clipboardul editorului (`editorClipboard`): un element copiat */
  const [clipboard, setClipboard] = useState<Clipboard | null>(() => citesteClipboardul());
  /** grinda lipită din clipboard: unealta Grindă pornește cu secțiunea ei */
  const [sectiuneLipita, setSectiuneLipita] = useState<SectiuneGrinda | null>(null);
  /** meniul de clic dreapta: unde s-a deschis (px în pânză, mm pe planșă)
   *  și pe ce — un element, sau planul gol */
  const josRef = useRef<(e: KeyboardEvent) => void>(() => {});
  const [meniu, setMeniu] = useState<{ x: number; y: number; p: Pt | null;
                                       tinta: { fel: "esv" | "grinda"; id: string } | null } | null>(null);
  const [verificare, setVerificare] =
    useState<"verificat" | "de_verificat" | "se_verifica" | "eroare">("verificat");
  const [eroriServer, setEroriServer] = useState<Record<number, string[]>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.erori])));
  const [avertismente, setAvertismente] = useState<Record<number, string[]>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.avertismente ?? []])));
  const [tipuriEsv, setTipuriEsv] = useState<Record<number, number>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.tipuri_esv])));
  // ROȘUL EDITORULUI e verdictul solverului (`editare.verdictele_amplasarii`):
  // la deschidere cel al geometriei de plecare, apoi cel al ultimei geometrii
  // verificate. null = serverul nu l-a putut da; rămâne regula client.
  const [verdicte, setVerdicte] = useState<Record<number, VerdictAmplasare[] | null>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.amplasare ?? null])));
  // PLACA ȘI OCHIURILE sunt ale FEM-ului (`editare.placile_editorului`), pe
  // geometria pe care le-a socotit serverul (`gPlaci`); ce s-a schimbat de
  // atunci se previzualizează (`editorPlaci.ochiuriVazute`)
  const [placi, setPlaci] = useState<Record<number, PlacaNivel[] | null>>(
    () => Object.fromEntries(editare.corpuri.map((c, i) => [i, c.placi ?? null])));
  const gPlaci = useRef<Record<number, Geometrie>>(
    Object.fromEntries(editare.corpuri.map((c, i) => [i, c.geometrie])));
  const [straturi, setStraturi] = useState<Straturi>(straturileTinute);
  const comutaStratul = (s: keyof Straturi) => setStraturi((x) => {
    const nou = { ...x, [s]: !x[s] };
    try { localStorage.setItem(CHEIE_STRATURI, JSON.stringify(nou)); } catch { /* fără memorie: rămâne pe ecran */ }
    return nou;
  });
  /** conturul consolei în lucru: `vechi` e conturul de la ultima schimbare
   *  scrisă în geometrie, `cur` cel de pe ecran */
  const [consolaEd, setConsolaEd] = useState<{ id: string; vechi: Pt[]; cur: Pt[] } | null>(null);
  const [coltAles, setColtAles] = useState<number | null>(null);
  const tragColt = useRef<{ i: number; p0: Pt; pornit: boolean } | null>(null);
  const [ghidPlaca, setGhidPlaca] = useState<{ axa: 0 | 1; valoare: number }[]>([]);
  /** placa desenată de la zero, colț cu colț (mm) */
  const [desen, setDesen] = useState<Pt[] | null>(null);
  /** ESV-urile schimbate de la ultima verificare, pe corp: până răspunde
   *  serverul, pe ele se arată regula client, ca previzualizare */
  const atinse = useRef<Record<number, Set<string>>>({});
  const [atinseVersiune, setAtinseVersiune] = useState(0);
  const [salvare, setSalvare] = useState<"nu" | "se_salveaza">("nu");
  /** corpurile schimbate față de sursă — ele pleacă la salvare */
  const schimbate = useRef<Set<number>>(new Set());
  const deVerificat = useRef<Set<number>>(new Set());
  const cronVerificare = useRef<ReturnType<typeof setTimeout> | null>(null);

  const corp = editare.corpuri[corpIdx] ?? null;
  const ctx = corp?.context ?? null;
  const G = geo[corpIdx] ?? null;
  const simetrie = corp?.simetrie ?? null;
  /** după o schimbare pe `ids` între `G0` și `G1` (poziție, secțiune, rotire):
   *  gemenii lor de simetrie o urmează oglindit (`urmeazaSimetria`), iar
   *  grinzile se așază pe toți cei atinși */
  const cuSimetria = (G0: Geometrie, G1: Geometrie, ids: readonly string[]): { G: Geometrie; ids: string[] } => {
    if (!simetric) return { G: G1, ids: [...ids] };
    const gem = gemeniiDeUrmat(simetrie, ids);
    return { G: urmeazaSimetria(G0, G1, ids, simetrie), ids: [...ids, ...gem.keys()] };
  };
  const scrieSim = (G0: Geometrie, G1: Geometrie, ids: readonly string[], inIstoric = true) => {
    const r = cuSimetria(G0, G1, ids);
    schimba(aseazaGrinzile(r.G, r.ids, ctx, G0), inIstoric);
  };
  const mutaSim = (G0: Geometrie, id: string, p: Pt): Geometrie => {
    const r = cuSimetria(G0, mutaEsv(G0, id, p), [id]);
    return aseazaGrinzile(r.G, r.ids, ctx, G0);
  };
  const niveluri = ctx?.niveluri ?? [];
  const nivelEf = nivel ?? indexParter(niveluri);
  const cadru = useMemo(() => (ctx ? cadrulContextului(ctx, G ?? undefined) : null),
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        [ctx]);
  const nivelCtx = niveluri[nivelEf] ?? null;
  const axe: AxaPerete[] = useMemo(
    () => (cadru && nivelCtx ? axelePeretilor(cadru, nivelCtx) : []), [cadru, nivelCtx]);
  /** axele arhitectului ale nivelului: capătul de grindă se lipește și unde
   *  taie ele elementul (`puncteDeAncorare`) */
  const axeArh: AxaArh[] = useMemo(() => nivelCtx?.axe ?? [], [nivelCtx]);
  // ghidajele de tragere ale axelor arhitectului, pe ecran (`lipesteNodul`)
  const ghidajeAxe = useMemo(() => (cadru ? ghidajeleAxelor(cadru, axeArh) : []), [cadru, axeArh]);

  // ---- verificarea ------------------------------------------------------------
  // Ciorna nu se scrie nicăieri: serverul o verifică și întoarce erorile,
  // avertismentele gărzilor și câte tipuri de ESV are corpul.
  const verificaAcum = useCallback(async () => {
    if (cronVerificare.current) { clearTimeout(cronVerificare.current); cronVerificare.current = null; }
    const care = [...deVerificat.current];
    if (!care.length || !editare) return true;
    deVerificat.current.clear();
    setVerificare("se_verifica");
    try {
      for (const i of care) {
        const g = geoRef.current[i];
        if (!g) continue;
        const r = await verificaEditare(projectId, sursa, editare.corpuri[i].cod, g);
        // ochiurile vin și pe o geometrie cu greșeli de schemă; fără ele
        // (Pasul 4 n-a mers) rămân cele de dinainte, previzualizate
        if (r.placi) {
          gPlaci.current[i] = g;
          setPlaci((x) => ({ ...x, [i]: r.placi ?? null }));
        }
        if (!r.erori?.length) {
          setVerdicte((v) => ({ ...v, [i]: r.amplasare ?? null }));
          // verdictul e al geometriei trimise: ce s-a schimbat între timp
          // (`deVerificat` îl are) rămâne pe previzualizare
          if (!deVerificat.current.has(i)) { atinse.current[i] = new Set(); setAtinseVersiune((x) => x + 1); }
        }
        setEroriServer((e) => ({ ...e, [i]: r.erori ?? [] }));
        setAvertismente((a) => ({ ...a, [i]: r.avertismente ?? [] }));
        if (typeof r.tipuri_esv === "number") setTipuriEsv((x) => ({ ...x, [i]: r.tipuri_esv }));
      }
      setVerificare(deVerificat.current.size ? "de_verificat" : "verificat");
      return true;
    } catch (err) {
      care.forEach((i) => deVerificat.current.add(i));
      setVerificare("eroare");
      setMesaj((err as Error).message);
      return false;
    }
  }, [editare, projectId, sursa]);
  const geoRef = useRef(geo);
  geoRef.current = geo;

  /** O schimbare a geometriei corpului curent: intră în istoric (`cuIstoric`)
   *  și pleacă spre server după pauză. În timpul unei trageri istoricul se
   *  scrie o singură dată, la capăt. */
  const schimba = useCallback((nou: Geometrie, cuIstoric = true) => {
    setGeo((g) => {
      const vechi = g[corpIdx];
      if (cuIstoric && vechi) {
        const st = trecut.current[corpIdx] ?? [];
        trecut.current[corpIdx] = [...st.slice(-ISTORIC_MAX + 1), vechi];
        viitor.current[corpIdx] = [];
      }
      if (vechi) {
        const a = atinse.current[corpIdx] ?? (atinse.current[corpIdx] = new Set());
        for (const id of esvSchimbate(vechi, nou)) a.add(id);
      }
      return { ...g, [corpIdx]: nou };
    });
    setAtinseVersiune((x) => x + 1);
    schimbate.current.add(corpIdx);
    deVerificat.current.add(corpIdx);
    setVerificare("de_verificat");
    if (cronVerificare.current) clearTimeout(cronVerificare.current);
    cronVerificare.current = setTimeout(() => { void verificaAcum(); }, VERIFICARE_MS);
  }, [corpIdx, verificaAcum]);

  const anuleaza = useCallback(() => {
    const st = trecut.current[corpIdx] ?? [];
    if (!st.length || !G) return;
    viitor.current[corpIdx] = [...(viitor.current[corpIdx] ?? []), G];
    trecut.current[corpIdx] = st.slice(0, -1);
    schimba(st[st.length - 1], false);
  }, [corpIdx, G, schimba]);
  const reface = useCallback(() => {
    const vi = viitor.current[corpIdx] ?? [];
    if (!vi.length || !G) return;
    trecut.current[corpIdx] = [...(trecut.current[corpIdx] ?? []), G];
    viitor.current[corpIdx] = vi.slice(0, -1);
    schimba(vi[vi.length - 1], false);
  }, [corpIdx, G, schimba]);

  // CIORNA NESALVATĂ: plecarea de pe ecran întreabă întâi (v. `nescris.ts`).
  const areCiorna = verificare !== "verificat" || schimbate.current.size > 0;
  useEffect(() => {
    if (!areCiorna || doarCitire) return;
    tineNescrisul(() => window.confirm(t("struct.editor.pleci_fara_salvare")));
    return () => tineNescrisul(null);
  }, [areCiorna, doarCitire, t]);
  useEffect(() => () => {
    if (cronVerificare.current) clearTimeout(cronVerificare.current);
  }, []);

  // ---- pânza ---------------------------------------------------------------
  const gazda = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View | null>(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });
  const bbox = useMemo(() => {
    if (!cadru || !ctx) return null;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const n of ctx.niveluri)
      for (const p of n.pereti)
        for (const q of p.colturi) {
          const [x, y] = laEcran(cadru, q);
          x0 = Math.min(x0, x); y0 = Math.min(y0, y);
          x1 = Math.max(x1, x); y1 = Math.max(y1, y);
        }
    return Number.isFinite(x0) ? [x0, y0, x1, y1] as [number, number, number, number] : null;
  }, [cadru, ctx]);
  const incadreaza = useCallback(() => {
    const el = gazda.current;
    if (!el || !bbox) return;
    const w = el.clientWidth || 800;
    const h = el.clientHeight || 500;
    setDim({ w, h });
    setView(fitBbox(bbox, w, h, { padM: 2, maxK: 60, minK: 2,
                                  insets: { top: 110, bottom: 60 } }));
  }, [bbox]);
  useEffect(() => { incadreaza(); }, [bbox]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setDim({ w: el.clientWidth, h: el.clientHeight }));
    obs.observe(el);
    return () => obs.disconnect();
  }, [editare]);
  const { panoramaPornita, spatiu } = useGesturi({
    gazda, view, setView, dim, incadreaza, poatePanorama: false,
    obiectAles: ales?.fel === "esv", kMin: K_MIN_EDITOR, kMax: K_MAX_EDITOR,
  });
  const k = view?.k ?? 20;

  // HÂRTIA DE CALC: planul arhitecturii peste geometrie (`calcArhitectura`).
  // Documentul se cere o singură dată, la prima aprindere, și rămâne
  // (`documentArhitectura`); aici se aduce la originea locală.
  const [calc, setCalc] = useState<Calc>(CALC_IMPLICIT);
  // axele arhitectului (grila modulară): se arată când desenul le are
  const [arataAxe, setArataAxe] = useState(true);
  const areAxe = useMemo(() => (ctx?.niveluri ?? []).some((n) => (n.axe ?? []).length > 0), [ctx]);
  // …derivate de structură din ziduri, nu desenate: se spune în titlu și se desenează altfel
  const axeDerivate = useMemo(
    () => (ctx?.niveluri ?? []).some((n) => (n.axe ?? []).some((a) => a.derivata)), [ctx]);
  const { doc: docArh, stare: docStare } =
    useDocumentArhitectura(projectId, editare.arhitectura, calc.vizibil);
  const origineArh = useMemo<[number, number]>(
    () => (docArh ? origineDesen(docArh) : [0, 0]), [docArh]);
  const planuriArh = useMemo(
    () => (docArh ? toStoreyPlans(laOrigineLocala(docArh, origineArh)) : []), [docArh, origineArh]);
  const planseeCalc = useMemo(
    () => (nivelCtx ? planseeleNivelului(planuriArh, nivelCtx, corp?.cod ?? null) : []),
    [planuriArh, nivelCtx, corp]);
  const transformareCalc = useMemo(
    () => (docArh && cadru && ctx ? transformarea(docArh.crs, origineArh, ctx.theta_grade, cadru) : ""),
    [docArh, cadru, ctx, origineArh]);
  // PLANȘA ARHITECTULUI: nivelul venit dintr-o foaie (DWG, DXF, PDF) arată
  // foaia însăși, nu interpretarea ei; fără foaie — sau cu foaia de necitit,
  // sau ștearsă din dosar după import — planul documentului, în convenția
  // tehnică (`desenPlansa`)
  const importul = useMemo(
    () => (docArh && nivelCtx ? importulPlansei(docArh, nivelCtx.storey) : null),
    [docArh, nivelCtx]);
  const planseArh = useRegistruOptional()?.registru?.solutii
    .find((s) => s.id === editare.arhitectura)?.planse;
  const { val: plansa, stare: starePlansa, stearsa: plansaStearsa } =
    useDesenPlansa(projectId, calc.vizibil ? importul : null, planseArh);
  const cuPlansa = importul !== null && !plansaStearsa && starePlansa !== "eroare";
  // COTELE TRONSONULUI: laturile conturului, cât se vede tot pe ecran — de
  // aproape, planul e al elementelor, iar cotele lui ar sta în afara ecranului
  const contur = useMemo(() => (cadru && nivelCtx ? conturulTronsonului(cadru, nivelCtx) : null),
                         [cadru, nivelCtx]);
  const tronsonIntreg = !!contur && !!view && dim.w > 0 && dim.h > 0
    && contur.every(([x, y]) => {
      const sx = view.tx + view.k * x;
      const sy = view.ty + view.k * y;
      return sx >= 0 && sx <= dim.w && sy >= 0 && sy <= dim.h;
    });
  const coteTronson = tronsonIntreg && contur ? coteleTronsonului(contur, 34 / k) : [];
  const tol = Math.min(0.4, 14 / k);        // cât prinde un zid, în metri
  const tolAx = Math.min(0.2, 10 / k);      // cât prinde un ax de nod

  const esvNivel = useMemo(
    () => (G ? G.esv.filter((e) => sectiunea(e, nivelEf)) : []), [G, nivelEf]);
  const nodurile = useCallback((fara?: string): Pt[] => (G && cadru
    ? G.esv.filter((e) => e.id !== fara).map((e) => laEcran(cadru, e.nod)) : []), [G, cadru]);
  const err = useMemo(() => (G ? greseli(G) : { grinzi: new Set<string>(), mesaje: [] }), [G]);
  // ESV-urile refuzate de verdictul solverului pe nivelul de pe ecran (în
  // cameră, prin gol), iar pe cele schimbate de la ultima verificare regula
  // client, ca previzualizare — numai avertismente: nu opresc salvarea
  const iesiri = useMemo(() => (G
    ? iesirileDeDesenat(G, nivelEf, nivelCtx, niveluri, verdicte[corpIdx], atinse.current[corpIdx] ?? new Set())
    : new Map<string, Iesire>()),
  [G, nivelEf, nivelCtx, niveluri, verdicte, corpIdx, atinseVersiune]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ---- placa nivelului ---------------------------------------------------------
  const placiCorp = useMemo(() => placi[corpIdx] ?? [], [placi, corpIdx]);
  const placaNiv = useMemo(() => placiCorp.find((p) => p.nivel === nivelEf) ?? null, [placiCorp, nivelEf]);
  const vazute = useMemo(() => {
    const Gv = gPlaci.current[corpIdx];
    if (!placaNiv || !G || !Gv) return { ochiuri: [] as OchiVazut[], previzualizare: false };
    try {
      return ochiuriVazute(placaNiv, G, Gv, nivelEf, zoneleSchimbate(Gv, G, nivelEf));
    } catch {
      // tăietorul n-a putut (un poligon degenerat): rămân ochiurile serverului
      return { ochiuri: placaNiv.ochiuri.map((o) => ({ id: o.id, poli: [o.contur, ...o.inele], arie_m2: o.arie_m2, server: o })),
               previzualizare: true };
    }
  }, [placaNiv, G, corpIdx, nivelEf]);
  const nuante = useMemo(() => nuanteleOchiurilor(vazute.ochiuri), [vazute]);
  const golurileOmului = useMemo(() => (G ? placiNivel(G, nivelEf).scoase : []), [G, nivelEf]);
  const ochiAles = ales?.fel === "ochi" ? vazute.ochiuri.find((o) => inPoligon(ales.p, o.poli)) ?? null : null;
  const golAles = ales?.fel === "gol"
    ? golurileOmului.find((r) => JSON.stringify(r) === JSON.stringify(ales.inel)) ?? null : null;
  /** ce strat se vede: al omului, sau aprins de unealta care are nevoie de el */
  const vede = (s: keyof Straturi) => straturi[s]
    || (s === "esv" && (unealta === "esv" || unealta === "grinda"))
    || (s === "grinzi" && unealta === "grinda")
    || (s === "pereti" && unealta === "esv")
    || (s === "ochiuri" && (unealta === "placa" || !!consolaEd));
  /** colțurile de care se lipește un colț de placă: ale plăcii și nodurile */
  const repere = (p: Pt): Pt[] => {
    const out: Pt[] = [];
    const raza = 3000;
    for (const poli of placaNiv?.slab ?? [])
      for (const r of poli)
        for (const q of r) if (Math.abs(q[0] - p[0]) < raza && Math.abs(q[1] - p[1]) < raza) out.push(q);
    for (const e of esvNivel) out.push(e.nod);
    return out;
  };

  // ---- gesturile -----------------------------------------------------------
  const gest = useRef<{ id: string; p0: Pt; nod0: Pt; G0: Geometrie; pornit: boolean } | null>(null);
  /** capătul liber al unei piese, tras: fața se mișcă, iar lăsată în fața
   *  altui ESV cele două se unesc */
  /** `altii`: membrii clasei elementului, luați la apăsare — în timpul
   *  tragerii elementul tras e deja în altă clasă, deci nu se mai pot afla */
  const trag = useRef<{ id: string; c: Capat; p0: Pt; G0: Geometrie; pornit: boolean;
                        vecin: string | null; altii: string[] } | null>(null);
  const [tragere, setTragere] = useState<{ id: string; c: Capat; poz: number;
                                          vecin: string | null; ghidaj: number | null } | null>(null);
  /** capătul grinzii alese, tras spre alt element: se reancorează la ridicare */
  const [capTras, setCapTras] = useState<{ gid: string; cap: "i" | "j"; p0: Pt; p: Pt } | null>(null);

  const punct = (e: { clientX: number; clientY: number }): Pt | null =>
    gazda.current && view ? laLume(e, gazda.current, view) : null;

  useEffect(() => { if (unealta !== "grinda") setSectiuneLipita(null); }, [unealta]);
  useEffect(() => { setCreare(false); }, [unealta]);

  const niveluriNoi = (): number[] => {
    const n = domeniu === "toate" ? niveluri.length - 1 : nivelEf;
    return Array.from({ length: n + 1 }, (_, i) => i);
  };

  /** Apăsarea vine din desen, nu din cardurile care plutesc peste el (bara de
   *  unelte, panoul, cotele scrise): ele stau în pânză, iar un clic pe
   *  «+ Grindă» ar fi pus altfel un ESV sub buton. */
  const dinDesen = (e: React.PointerEvent) =>
    !!(e.target as Element).closest?.("svg.ob-es-svg")
    && !(e.target as Element).closest?.("foreignObject");

  const pePanza = (e: React.PointerEvent) => {
    if (!dinDesen(e)) return;
    if (meniu) setMeniu(null);
    if (creare) setCreare(false);
    if (panoramaPornita(e)) return;
    if (e.button !== 0 || doarCitire || !G || !cadru) return;
    const p = punct(e);
    if (!p) return;
    setCotaScrisa(null);
    if (unealta === "esv") {
      const l = lipesteNodul(p, axe, nodurile(), tol, tolAx, ghidajeAxe);
      const ax = l.perete?.axa ?? "X";
      const b = l.perete ? Math.max(LATURA_MIN_CM,
                                    Math.round((l.perete.grosime * 100) / 5) * 5) : 40;
      const L = l.perete ? 60 : 40;
      const r = adaugaEsv(G, { nod: laPlansa(cadru, l.p), axa: ax, b_cm: b, L_cm: L,
                               niveluri: niveluriNoi() });
      // pus pe o grindă: ea se împarte în două, ancorate din nou pe element
      const sub = grindaSubPunct(G, laPlansa(cadru, p), nivelEf, tol * 1000);
      const imp = sub ? imparteGrinda(r.G, sub.id, r.id, ctx) : null;
      schimba(imp ? imp.G : r.G);
      setMesaj(null);
      // elementul pus rămâne ales, iar unealta se întoarce la «Selectează»
      setAles({ fel: "esv", id: r.id });
      setUnealta("alege");
      setLipire(null);
      setGrindaSub(null);
      return;
    }
    if (unealta === "grinda") { setGrindaDe(null); return; }
    if (unealta === "placa") {
      const pm = laPlansa(cadru, p);
      const pts = desen ?? [];
      // clic pe primul colț: placa se închide
      if (pts.length >= 3 && (Math.hypot(pts[0][0] - pm[0], pts[0][1] - pm[1]) / 1000) * k < 10) {
        inchideDesenul(pts);
        return;
      }
      const l = lipesteColt(pm, [...pts.slice(-1), ...pts.slice(0, 1), ...repere(pm)], tolColt);
      setDesen([...pts, l.p]);
      setGhidPlaca(l.ghidaje);
      return;
    }
    setAles(null);
    setConsolaEd(null);
  };

  /** Clic pe un ochi de placă: se alege (cu celelalte unelte clicul trece la
   *  pânză — ochiul stă sub tot ce se pune pe el). */
  const peOchi = (e: React.PointerEvent, o: OchiVazut) => {
    if (e.button !== 0 || unealta !== "alege") return;
    const p = punct(e);
    if (!p || !cadru) return;
    e.stopPropagation();
    setCotaScrisa(null);
    setConsolaEd(null);
    setAles({ fel: "ochi", p: o.server ? interior(o.poli) : laPlansa(cadru, p) });
  };
  const peGol = (e: React.PointerEvent, inel: Pt[]) => {
    if (e.button !== 0 || unealta !== "alege") return;
    e.stopPropagation();
    setConsolaEd(null);
    setAles({ fel: "gol", inel });
  };
  /** Colțul consolei în lucru: tras se mută, cu Alt se scoate. */
  const peColt = (e: React.PointerEvent, i: number) => {
    if (e.button !== 0 || doarCitire || !consolaEd) return;
    e.stopPropagation();
    if (e.altKey) {
      const r = scoateColt(consolaEd.cur, i);
      if (r) scrieConturul(r); else setMesaj(t("struct.editor.placa_trei_colturi"));
      setColtAles(null);
      return;
    }
    setColtAles(i);
    const p = punct(e);
    if (p) tragColt.current = { i, p0: p, pornit: false };
    try { gazda.current?.setPointerCapture(e.pointerId); } catch { /* fără captură: merge și așa */ }
  };
  /** «+» pe mijlocul laturii `i`: un colț nou, care se poate trage pe loc. */
  const peMijloc = (e: React.PointerEvent, i: number) => {
    if (e.button !== 0 || doarCitire || !consolaEd) return;
    e.stopPropagation();
    setConsolaEd({ ...consolaEd, cur: adaugaColt(consolaEd.cur, i) });
    setColtAles(i + 1);
    const p = punct(e);
    if (p) tragColt.current = { i: i + 1, p0: p, pornit: false };
    try { gazda.current?.setPointerCapture(e.pointerId); } catch { /* fără captură: merge și așa */ }
  };

  const peEsv = (e: React.PointerEvent, id: string) => {
    if (e.button !== 0 || !G || !cadru) return;
    // colțul plăcii noi se poate pune și pe un element
    if (unealta === "placa" && !doarCitire) return;
    e.stopPropagation();
    setCotaScrisa(null);
    if (doarCitire) { setAles({ fel: "esv", id }); return; }
    if (unealta === "grinda") {
      const el = G.esv.find((x) => x.id === id);
      const p = punct(e);
      if (!el || !p) return;
      const pm = laPlansa(cadru, p);
      const piesa = piesaSubPunct(el, nivelEf, pm);
      const liber = e.altKey || altApasat;
      if (!grindaDe || grindaDe.id === id) {
        // primul capăt: piesa de care pleacă grinda — sau, cu Alt, punctul
        // lăsat de om, lipit de cele de ancorare (`ancorareaPeEsv`)
        const anc = liber ? ancorareaPeEsv(el, nivelEf, pm, tol * 1000, null, axeArh) : null;
        setGrindaDe({ id, piesa, p: anc?.p ?? null });
        return;
      }
      const a = G.esv.find((x) => x.id === grindaDe.id)!;
      // capetele: pe linia comună a pieselor — paralelă sau perpendiculară pe
      // ele —, altfel unde a dat omul clic (`capeteleGrinziiNoi`)
      const sec = sectiuneLipita ?? sectiuneaObisnuita(G, nivelEf);
      const cap = capeteleGrinziiNoi(a, grindaDe, el, piesa, nivelEf, pm, tol * 1000, axeArh, liber,
                                     10 * sec.b_cm);
      if (!cap) return;
      const capete = { a: cap.a, b: cap.b };
      // după a doua apăsare unealta se întoarce la «Selectează», cu grinda
      // aleasă — ca la ESV și la placă: o adăugare, o unealtă
      const gata = (gid: string) => {
        setGrindaDe(null);
        setUnealta("alege");
        setAles({ fel: "grinda", id: gid });
      };
      const exista = grindaIntre(G, a.id, id, nivelEf, capete);
      if (exista) {
        setMesaj(t("struct.editor.grinda_exista", { a: a.id, b: id }));
        gata(exista.id);
        return;
      }
      const niv = domeniu === "toate" ? niveluri.map((n) => n.index) : [nivelEf];
      const r = adaugaGrinda(G, a.id, id, niv, sec, capete);
      if (!r) { setMesaj(t("struct.editor.grinda_fara_nivel")); return; }
      schimba(r.G);
      // oblică, nu refuzată: omul poate s-o îndrepte apoi de la oricare capăt
      // — sau nu, când betonul celor două nu ține lățimea grinzii pe nicio direcție
      const gol = greseli(r.G).grinzi.has(r.id) ? golulDeLinie(a, el, nivelEf, 10 * sec.b_cm) : null;
      setMesaj(!greseli(r.G).grinzi.has(r.id) ? null
        : liber || !gol || gol.mm <= 0
          ? t("struct.editor.grinda_oblica", { g: r.id, a: a.id, b: id })
          : t("struct.editor.fara_linie_comuna", { g: r.id, a: a.id, b: id,
                                                    d: (gol.mm / 10).toFixed(0), axa: gol.axa }));
      gata(r.id);
      return;
    }
    setAles({ fel: "esv", id });
    if (unealta === "alege") {
      const p = punct(e);
      const el = G.esv.find((x) => x.id === id);
      if (p && el) gest.current = { id, p0: p, nod0: laEcran(cadru, el.nod), G0: G, pornit: false };
    }
  };

  const peCapat = (e: React.PointerEvent, gid: string, cap: "i" | "j") => {
    if (e.button !== 0 || doarCitire) return;
    e.stopPropagation();
    const p = punct(e);
    if (p) setCapTras({ gid, cap, p0: p, p });
  };

  const peCapatLiber = (e: React.PointerEvent, id: string, c: Capat) => {
    if (e.button !== 0 || doarCitire || !G) return;
    e.stopPropagation();
    setCotaScrisa(null);
    setAles({ fel: "esv", id });
    const p = punct(e);
    if (!p) return;
    // fără captură, o ridicare în afara pânzei ar lăsa tragerea agățată; pe
    // pânză, nu pe mâner: mânerul unui braț tras până la zero dispare
    try { gazda.current?.setPointerCapture(e.pointerId); } catch { /* fără captură: merge și așa */ }
    trag.current = { id, c, p0: p, G0: G, pornit: false, vecin: null,
                     altii: peClasa ? membrii(G, id, nivelEf).filter((m) => m !== id) : [] };
  };

  const peGrinda = (e: React.PointerEvent, id: string) => {
    if (e.button !== 0) return;
    // cu unealta ESV, grinda e doar ce stă peste zid: elementul se pune acolo;
    // cu unealta plăcii, colțul plăcii noi se pune și pe grindă
    if ((unealta === "esv" || unealta === "placa") && !doarCitire) return;
    e.stopPropagation();
    setCotaScrisa(null);
    if (unealta === "grinda" && !doarCitire) return;
    setAles({ fel: "grinda", id });
  };

  const miscare = (e: React.PointerEvent) => {
    if (!gest.current && !capTras && !trag.current && !tragColt.current && !dinDesen(e)) return;
    const p = punct(e);
    setCursor(p);
    if (!p || !G || !cadru) return;
    if (capTras) { setCapTras({ ...capTras, p }); return; }
    const tc = tragColt.current;
    if (tc && consolaEd) {
      if (!tc.pornit && Math.hypot(p[0] - tc.p0[0], p[1] - tc.p0[1]) * k < 3) return;
      tc.pornit = true;
      const cur = consolaEd.cur;
      const n = cur.length;
      const pm = laPlansa(cadru, p);
      const l = lipesteColt(pm, [cur[(tc.i + n - 1) % n], cur[(tc.i + 1) % n], ...repere(pm)], tolColt);
      setConsolaEd({ ...consolaEd, cur: mutaColt(cur, tc.i, l.p) });
      setGhidPlaca(l.ghidaje);
      return;
    }
    if (unealta === "placa" && desen?.length) {
      const pm = laPlansa(cadru, p);
      setGhidPlaca(lipesteColt(pm, [desen[desen.length - 1], desen[0], ...repere(pm)], tolColt).ghidaje);
    }
    const tr = trag.current;
    if (tr) {
      const d: Pt = [p[0] - tr.p0[0], p[1] - tr.p0[1]];
      if (!tr.pornit && Math.hypot(d[0], d[1]) * k < 3) return;
      tr.pornit = true;
      const e0 = tr.G0.esv.find((x) => x.id === tr.id);
      if (!e0) return;
      // deplasarea pe axa feței, în mm ai planșei (Y-ul ecranului e întors)
      const dmm = tr.c.axa === 0 ? d[0] * 1000 : -d[1] * 1000;
      const a = undeAjunge(tr.G0, e0, nivelEf, tr.c, tr.c.poz + dmm, nivelCtx,
                           (LIPIRE_FATA_PX / k) * 1000);
      tr.vecin = a.vecin;
      setTragere({ id: tr.id, c: tr.c, poz: a.poz, vecin: a.vecin, ghidaj: a.ghidaj });
      // «Toată clasa»: membrii urmează deodată latura trasă (`urmeazaClasa`)
      const dom = domeniu === "toate" ? null : nivelEf;
      const G1 = trageCapatul(tr.G0, tr.id, nivelEf, dom, tr.c, a.poz);
      scrieSim(tr.G0, urmeazaClasa(tr.G0, G1, tr.id, nivelEf, dom, tr.altii), [tr.id, ...tr.altii], false);
      return;
    }
    const g = gest.current;
    if (g) {
      const d: Pt = [p[0] - g.p0[0], p[1] - g.p0[1]];
      if (!g.pornit && Math.hypot(d[0], d[1]) * k < 3) return;
      g.pornit = true;
      const l = lipesteNodul([g.nod0[0] + d[0], g.nod0[1] + d[1]], axe, nodurile(g.id), tol, tolAx,
                             ghidajeAxe);
      setLipire(l);
      schimba(mutaSim(g.G0, g.id, laPlansa(cadru, l.p)), false);
      return;
    }
    if (unealta === "esv" && !doarCitire) {
      setLipire(lipesteNodul(p, axe, nodurile(), tol, tolAx, ghidajeAxe));
      setGrindaSub(grindaSubPunct(G, laPlansa(cadru, p), nivelEf, tol * 1000)?.id ?? null);
    } else {
      if (lipire) setLipire(null);
      if (grindaSub) setGrindaSub(null);
    }
  };

  const ridicare = () => {
    const tc = tragColt.current;
    if (tc) {
      tragColt.current = null;
      setGhidPlaca([]);
      if (tc.pornit && consolaEd) scrieConturul(consolaEd.cur);
      return;
    }
    const tr = trag.current;
    if (tr) {
      trag.current = null;
      setTragere(null);
      if (!tr.pornit) return;
      const acum = geoRef.current[corpIdx];
      if (tr.vecin && acum) {
        // lăsată în fața vecinului: cele două devin un singur element
        const r = unesteEsv(acum, tr.id, tr.vecin, tr.c);
        if ("G" in r) {
          schimba(r.G, false);
          setAles({ fel: "esv", id: r.id });
          setMesaj(r.grinzi.length
            ? t("struct.editor.unite_grinzi", { a: r.id, b: r.inghitit, g: r.grinzi.join(", ") })
            : t("struct.editor.unite", { a: r.id, b: r.inghitit }));
        } else {
          setMesaj(t(`struct.editor.unire_${r.eroare}` as Cheie,
                     { a: tr.id, b: tr.vecin, esv: r.esv ?? "" }));
        }
      }
      // un singur pas în istoric pentru toată tragerea, cu unirea cu tot
      trecut.current[corpIdx] = [...(trecut.current[corpIdx] ?? []), tr.G0].slice(-ISTORIC_MAX);
      viitor.current[corpIdx] = [];
      return;
    }
    if (capTras && G && cadru) {
      const { gid, cap, p0, p } = capTras;
      setCapTras(null);
      if (Math.hypot(p[0] - p0[0], p[1] - p0[1]) * k < 3) return;
      const r = reancoreazaCapatul(G, gid, cap, laPlansa(cadru, p), nivelEf, tol * 1000, axeArh,
                                   altApasat);
      if ("G" in r) {
        if (r.G !== G) schimba(r.G);
        const alt = G.grinzi.find((x) => x.id === gid);
        const idAlt = alt ? (cap === "i" ? alt.nod_j : alt.nod_i) : "";
        setMesaj(!r.oblica ? null
          : r.liniePosibila ? t("struct.editor.grinda_oblica", { g: gid, a: r.esv, b: idAlt })
          : t("struct.editor.capat_fara_linie", { g: gid, esv: r.esv, alt: idAlt }));
      } else if (r.eroare === "niveluri") {
        setMesaj(t("struct.editor.capat_niveluri", {
          g: gid, esv: r.esv ?? "",
          niveluri: (r.lipsa ?? []).map((n) => niveluri[n]?.nivel ?? n).join(", "),
        }));
      } else if (r.eroare === "acelasi") {
        setMesaj(t("struct.editor.capat_acelasi", { g: gid, esv: r.esv ?? "" }));
      }
      return;
    }
    const g = gest.current;
    gest.current = null;
    setLipire(null);
    if (g?.pornit) {
      // un singur pas în istoric pentru toată tragerea
      trecut.current[corpIdx] = [...(trecut.current[corpIdx] ?? []), g.G0].slice(-ISTORIC_MAX);
      viitor.current[corpIdx] = [];
    }
  };

  // ---- operațiile pe ce e ales --------------------------------------------
  const esvAles = ales?.fel === "esv" ? G?.esv.find((x) => x.id === ales.id) ?? null : null;
  const grindaAleasa = ales?.fel === "grinda" ? G?.grinzi.find((x) => x.id === ales.id) ?? null : null;

  // ---- operațiile pe placă ---------------------------------------------------------
  // Pe «Toate nivelurile» o schimbare merge și pe nivelurile cu «același» ochi
  // sau «aceeași» consolă — cea mai suprapusă cu ea (`peAlteNiveluri`): balconul
  // și ochiul se repetă pe etaje.
  const peToate = domeniu === "toate";
  const tolColt = (9 / k) * 1000;
  const scoateOchiul = (o: OchiVazut) => {
    if (!G || doarCitire) return;
    let G2 = scoatePlaca(G, nivelEf, o.poli[0]);
    if (peToate)
      for (const x of peAlteNiveluri(placiCorp, nivelEf, o.poli[0], (p) => p.ochiuri, ACELASI_OCHI))
        G2 = scoatePlaca(G2, x.nivel, x.el.contur);
    schimba(G2);
  };
  const puneGolul = (inel: Pt[]) => {
    if (!G || doarCitire) return;
    let G2 = punePlacaLaLoc(G, nivelEf, inel);
    if (peToate)
      for (const n of niveluri)
        if (n.index !== nivelEf)
          for (const r of placiNivel(G2, n.index).scoase)
            if (suprapunere(r, inel) >= ACELASI_GOL) G2 = punePlacaLaLoc(G2, n.index, r);
    schimba(G2);
    setAles(null);
  };
  const scoateConsola = (c: ConsolaPlaca) => {
    if (!G || doarCitire) return;
    let G2 = scoatePlaca(G, nivelEf, c.contur);
    if (peToate)
      for (const x of peAlteNiveluri(placiCorp, nivelEf, c.contur, (p) => p.console, ACEEASI_CONSOLA))
        G2 = scoatePlaca(G2, x.nivel, x.el.contur);
    schimba(G2);
    setConsolaEd(null);
  };
  const incepeConturul = (c: ConsolaPlaca) => {
    const r = simplificaInel(c.contur);
    setConsolaEd({ id: c.id, vechi: r, cur: r });
    setColtAles(null);
    setUnealta("alege");
  };
  /** conturul de pe ecran scris în geometrie: ce a crescut se adaugă, ce a
   *  scăzut se scoate — și pe nivelurile cu aceeași consolă */
  const scrieConturul = (cur: Pt[]) => {
    if (!G || !consolaEd) return;
    const { plus, minus } = diferentaContur(consolaEd.vechi, cur);
    if (plus.length || minus.length) {
      let G2 = schimbaPlaca(G, nivelEf, plus, minus);
      if (peToate)
        for (const x of peAlteNiveluri(placiCorp, nivelEf, consolaEd.vechi, (p) => p.console, ACEEASI_CONSOLA))
          G2 = schimbaPlaca(G2, x.nivel, plus, minus);
      schimba(G2);
    }
    setConsolaEd({ ...consolaEd, vechi: cur, cur });
  };
  /** placa desenată de la zero se închide: intră în placa nivelului ăstuia */
  const inchideDesenul = (pts: Pt[]) => {
    setDesen(null);
    setGhidPlaca([]);
    const curat = pts.filter((p, i) => i === 0 || Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]) > 1);
    if (!G || curat.length < 3) return;
    schimba(adaugaPlaca(G, nivelEf, curat));
    setUnealta("alege");
    setMesaj(t("struct.editor.placa_desenata"));
  };

  const sterge = useCallback(() => {
    if (!G || !ales || doarCitire) return;
    if (ales.fel === "esv") {
      const e = G.esv.find((x) => x.id === ales.id);
      if (!e) return;
      // pe nivelul ăsta = de aici în sus: un element nu poate lipsi la mijloc
      schimba(stergeEsv(G, ales.id, domeniu === "toate" ? null : nivelEf));
    } else if (ales.fel === "grinda") {
      schimba(stergeGrinda(G, ales.id, domeniu === "toate" ? null : nivelEf));
    } else {
      return;
    }
    setAles(null);
  }, [G, ales, doarCitire, domeniu, nivelEf, schimba]);

  const roteste = useCallback(() => {
    if (!G || !esvAles || doarCitire) return;
    scrieSim(G, rotesteEsv(G, esvAles.id), [esvAles.id]);
  }, [G, esvAles, doarCitire, schimba, ctx]);

  const redim = (m: { b_cm?: number; L_cm?: number }) => {
    if (!G || !esvAles || doarCitire) return;
    const b = m.b_cm != null ? Math.max(LATURA_MIN_CM, Math.round(m.b_cm)) : undefined;
    const L = m.L_cm != null ? Math.max(LATURA_MIN_CM, Math.round(m.L_cm)) : undefined;
    const ids = tintele(esvAles.id);
    scrieSim(G, peMembri(G, ids, (g, id) => redimEsv(g, id, domeniu === "toate" ? null : nivelEf,
                                                      { b_cm: b, L_cm: L })), ids);
  };

  /** copierea elementului `id` în clipboardul editorului */
  const copiaza = (id: string) => {
    if (!G) return;
    const c = copie(G, id);
    if (!c) return;
    setClipboard(c);
    tineClipboardul(c);
    setMesaj(t("struct.editor.copiat", { c: numeleCopiei(c, nivelEf) }));
  };

  /** lipirea elementului din clipboard: un ESV cade în punctul `pEcran`
   *  (lipit de zid și de axe, ca unealta ESV); o grindă intră în unealta
   *  Grindă cu secțiunea ei, fiindcă n-are loc fără două capete */
  const lipesteElementul = (pEcran: Pt | null) => {
    if (!G || !cadru || !clipboard || doarCitire) return;
    if (clipboard.fel === "grinda") {
      const sec = sectiuneaGrinziiCopiate(clipboard, nivelEf);
      if (!sec) return;
      setSectiuneLipita(sec);
      setUnealta("grinda"); setCreare(true); setGrindaDe(null); setConsolaEd(null); setDesen(null);
      setMesaj(t("struct.editor.grinda_din_copie", { s: numeleCopiei(clipboard, nivelEf) }));
      return;
    }
    if (!pEcran) return;
    const l = lipesteNodul(pEcran, axe, nodurile(), tol, tolAx, ghidajeAxe);
    const r = lipesteEsv(G, clipboard, laPlansa(cadru, l.p), niveluri.map((n) => n.index),
                         l.perete?.axa ?? null);
    if (!r) { setMesaj(t("struct.editor.lipit_fara_nivel")); return; }
    schimba(aseazaGrinzile(r.G, [r.id], ctx, G));
    setUnealta("alege");
    setAles({ fel: "esv", id: r.id });
    setMesaj(t("struct.editor.lipit", { id: r.id, s: clipboard.sursa }));
  };

  /** lipirea proprietăților din clipboard pe elementul `id` — și pe clasa
   *  lui, când omul a ales «Toată clasa» */
  const lipesteProp = (id: string) => {
    if (!G || !clipboard || doarCitire || !sePoateLipiPe(G, clipboard, id)) return;
    const ids = peClasa ? membrii(G, id, nivelEf) : [id];
    const G2 = peMembri(G, ids, (g, m) => lipesteProprietati(g, clipboard, m, nivelEf,
                                                            domeniu === "toate" ? null : nivelEf) ?? g);
    if (G2 === G) return;
    scrieSim(G, G2, ids);
    setAles({ fel: clipboard.fel, id });
    setMesaj(ids.length > 1 ? t("struct.editor.lipit_prop", { s: clipboard.sursa, n: ids.length })
      : t("struct.editor.lipit_prop_unu", { s: clipboard.sursa, id }));
  };

  /** clic dreapta pe un element sau pe plan: meniul contextual */
  const deschideMeniul = (e: React.MouseEvent, tinta: { fel: "esv" | "grinda"; id: string } | null) => {
    e.preventDefault();
    if (doarCitire || !gazda.current) return;
    e.stopPropagation();
    const r = gazda.current.getBoundingClientRect();
    setMeniu({ x: e.clientX - r.left, y: e.clientY - r.top, p: punct(e), tinta });
    if (tinta) { setAles(tinta); setConsolaEd(null); }
  };

  const scrieCota = (c: CotaEsv, v: number) => {
    setCotaScrisa(null);
    if (!G || !esvAles || !cadru || !(v > 0)) return;
    if (c.fel === "dist") {
      const d = deplasareaCotei(c, v);
      const nod = laEcran(cadru, esvAles.nod);
      schimba(mutaSim(G, esvAles.id, laPlansa(cadru, [nod[0] + d[0], nod[1] + d[1]])));
      return;
    }
    redim(c.fel === "L" ? { L_cm: v * 100 } : { b_cm: v * 100 });
  };

  // tastele editorului: ascultătorul se leagă o dată, iar corpul lui se
  // reînnoiește la fiecare randare printr-un ref — altfel o tastă ar vedea
  // starea de la ultima legare (cursorul, clipboardul, meniul), nu pe cea de
  // acum
  const scrie = (el: EventTarget | null) => {
    const x = el as HTMLElement | null;
    return !!x && (/^(INPUT|TEXTAREA|SELECT)$/.test(x.tagName) || x.isContentEditable);
  };
  josRef.current = (e: KeyboardEvent) => {
    if (scrie(e.target)) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "z") {
      e.preventDefault();
      if (e.shiftKey) reface(); else anuleaza();
      return;
    }
    // ⌘C copiază elementul ales; ⌘V lipește un element sub cursor; ⇧⌘V
    // lipește proprietățile pe elementul ales — nu în clipboardul
    // sistemului, ci în al editorului (`editorClipboard`)
    if (mod && e.key.toLowerCase() === "c" && !doarCitire) {
      if (ales && (ales.fel === "esv" || ales.fel === "grinda")) { e.preventDefault(); copiaza(ales.id); }
      return;
    }
    if (mod && e.key.toLowerCase() === "v" && !doarCitire) {
      e.preventDefault();
      if (e.shiftKey) { if (ales && (ales.fel === "esv" || ales.fel === "grinda")) lipesteProp(ales.id); }
      else lipesteElementul(cursor);
      return;
    }
    if (mod) return;
    const b = e.key.toLowerCase();
    if (b === "escape" && trag.current) {
      // tragerea se lasă: fața se întoarce unde era
      if (trag.current.pornit) schimba(trag.current.G0, false);
      trag.current = null;
      setTragere(null);
    }
    else if (b === "escape" && meniu) setMeniu(null);
    else if (b === "escape" && creare) setCreare(false);
    else if (b === "escape") {
      setCapTras(null); setGrindaDe(null); setAles(null); setUnealta("alege"); setCotaScrisa(null);
      setDesen(null); setConsolaEd(null); setGhidPlaca([]);
    }
    else if (doarCitire) return;
    else if (b === "enter" && desen && desen.length >= 3) { e.preventDefault(); inchideDesenul(desen); }
    else if (b === "enter" && consolaEd) { e.preventDefault(); setConsolaEd(null); }
    else if (b === "delete" || b === "backspace") {
      e.preventDefault();
      if (desen?.length) setDesen(desen.slice(0, -1));
      else if (consolaEd && coltAles != null) {
        const r = scoateColt(consolaEd.cur, coltAles);
        if (r) scrieConturul(r); else setMesaj(t("struct.editor.placa_trei_colturi"));
        setColtAles(null);
      }
      else if (ochiAles) scoateOchiul(ochiAles);
      else sterge();
    }
    else if (b === "r") roteste();
    else if (b === "v") setUnealta("alege");
    else if (b === "c") setUnealta("esv");
    else if (b === "b") setUnealta("grinda");
    else if (b === "p") { setUnealta("placa"); setConsolaEd(null); }
    else if (esvAles && cadru && G && /^arrow/.test(b)) {
      e.preventDefault();
      const pas = e.shiftKey ? 0.01 : 0.05;
      const d: Pt = b === "arrowleft" ? [-pas, 0] : b === "arrowright" ? [pas, 0]
        : b === "arrowup" ? [0, -pas] : [0, pas];
      const nod = laEcran(cadru, esvAles.nod);
      schimba(mutaSim(G, esvAles.id, laPlansa(cadru, [nod[0] + d[0], nod[1] + d[1]])));
    }
  };
  useEffect(() => {
    const jos = (e: KeyboardEvent) => josRef.current(e);
    window.addEventListener("keydown", jos);
    return () => window.removeEventListener("keydown", jos);
  }, []);

  // ---- calculul ------------------------------------------------------------
  const toateGreselile = useMemo(() => {
    const out: string[] = [];
    (editare?.corpuri ?? []).forEach((c, i) => {
      const lista = i === corpIdx ? [...err.mesaje, ...(eroriServer[i] ?? []).filter(
        (m) => !/oblică/.test(m))] : (eroriServer[i] ?? []);
      for (const m of lista) out.push(c.cod ? `${c.cod}: ${m}` : m);
    });
    return out;
  }, [editare, corpIdx, err, eroriServer]);

  /** «Salvează ca Soluția N+1»: toate corpurile schimbate, deodată. */
  const salveaza = async () => {
    if (salvare !== "nu") return;
    setMesaj(null);
    setSalvare("se_salveaza");
    const ok = await verificaAcum();
    if (!ok) { setSalvare("nu"); return; }
    try {
      const corpuri = [...schimbate.current]
        .map((i) => ({ cod: editare.corpuri[i].cod, geometrie: geoRef.current[i] }))
        .filter((c) => !!c.geometrie);
      const r = await salveazaCaSolutieNoua(projectId, sursa, corpuri);
      schimbate.current.clear();
      tineNescrisul(null);
      laSalvat(r);
    } catch (e) {
      setMesaj((e as Error).message);
    } finally {
      setSalvare("nu");
    }
  };
  const renunta = () => {
    if (schimbate.current.size && !window.confirm(t("struct.editor.renunti"))) return;
    schimbate.current.clear();
    tineNescrisul(null);
    laInchide();
  };

  // ---- desenul -------------------------------------------------------------
  if (!G || !cadru || !ctx) {
    return <div className="ob-studio-gol"><div><p>{t("struct.editor.se_incarca")}</p></div></div>;
  }

  const poli = (pts: Pt[]) => pts.map((q) => laEcran(cadru, q).join(",")).join(" ");
  const esvDedesubt = nivelEf > 0
    ? G.esv.filter((e) => !sectiunea(e, nivelEf) && sectiunea(e, nivelEf - 1)) : [];
  const grinziNivel = G.grinzi.filter((g) => g.sectiune_per_nivel[String(nivelEf)]);
  // clasele nivelului de pe ecran (`editorClase`): lista din stânga, cipul
  // din panou și conturul membrilor pe plan. Membrii clasei elementului ales
  // se văd mereu, stins; o clasă aprinsă cu mouse-ul îi ia locul
  const claseNivel = clasele(G, nivelEf, (k) => t(k as Cheie));
  const cheiaAleasa = esvAles ? cheiaEsv(esvAles, nivelEf)
    : grindaAleasa ? cheiaGrinzii(grindaAleasa, nivelEf) : null;
  const clasaAleasa = cheiaAleasa ? claseNivel.find((c) => c.cheie === cheiaAleasa) ?? null : null;
  const clasaVazuta = clasaAprinsa ?? cheiaAleasa;
  /** gemenii de simetrie ai elementului ales: conturul lor pe plan, cipul din panou */
  const gemeniAles = useMemo(() => new Set(esvAles ? geamanii(simetrie, esvAles.id).map((g) => g.id) : []),
                             [esvAles, simetrie]);
  const cipGemeni = esvAles && gemeniAles.size > 0 && (
    <div className="ob-es-gemeni">
      <span className="ob-es-clasa-cip gemeni" title={t("struct.editor.gemeni_titlu")}>
        {t("struct.editor.gemeni", { ids: [...gemeniAles].join(", ") })}
      </span>
      <button className={`ob-es-strat${simetric ? " on" : ""}`} aria-pressed={simetric} disabled={doarCitire}
              title={t("struct.editor.simetric_titlu")} onClick={() => setSimetric(!simetric)}>
        <i style={{ background: "#0b7f92" }} />{t("struct.editor.simetric")}
      </button>
    </div>
  );
  /** grupurile de simetrie ale ESV-ului ales: orbita, clasa de oglindă,
   *  perechea și geamănul (`grupurileLui`); un clic pe unul îi selectează
   *  membrii pe plan */
  const grupuriAles: GrupSimetrie[] = esvAles ? grupurileLui(simetrie, G.esv, esvAles.id) : [];
  const metri = (mm: number) => (mm / 1000).toFixed(2).replace(".", ",");
  // oglinda se numește după direcția pe care întoarce: poziția ei e în
  // coordonatele desenului (sute de kilometri pe un plan georeferențiat)
  const textulOperatiei = (o: Operatie) => o.fel === "oglinda"
    ? t("struct.editor.grup_op_oglinda", { d: o.directie.toLowerCase() })
    : t("struct.editor.grup_op_translatie", { d: o.directie, m: metri(o.pas_mm) });
  const numeleGrupului = (g: GrupSimetrie, id: string) => {
    const altul = g.membri.find((m) => m !== id) ?? "";
    switch (g.fel) {
      case "orbita": return t("struct.editor.grup_orbita", { o: g.orbita ?? "" });
      case "clasa": return t("struct.editor.grup_clasa");
      case "pereche": return t("struct.editor.grup_pereche", { id: altul });
      default: return t("struct.editor.grup_geaman", { id: altul });
    }
  };
  const detaliulGrupului = (g: GrupSimetrie) => [
    g.membri.join(", "),
    ...g.operatii.map(textulOperatiei),
    ...(g.fel === "orbita" && g.identica === false ? [t("struct.editor.grup_cu_abateri")] : []),
  ].join(" · ");
  const panouGrupuri = esvAles && grupuriAles.length > 0 && (
    <div className="ob-es-grupuri">
      <label title={t("struct.editor.grupuri_titlu")}>{t("struct.editor.grupuri")}</label>
      <ul className="ob-es-grupuri-lista">
        {grupuriAles.map((g) => (
          <li key={g.cheie}>
            <button className={grupAles?.cheie === g.cheie ? "on" : ""}
                    aria-pressed={grupAles?.cheie === g.cheie}
                    title={t(`struct.editor.grup_${g.fel}_titlu` as Cheie)}
                    onMouseEnter={() => setGrupAprins(g.membri)} onMouseLeave={() => setGrupAprins(null)}
                    onClick={() => setGrupAles(grupAles?.cheie === g.cheie ? null
                      : { cheie: g.cheie, membri: g.membri })}>
              <i style={{ background: "#0b7f92" }} />
              <span className="ob-es-grup-text">
                <span>{numeleGrupului(g, esvAles.id)}</span>
                <span className="sg-muted">{detaliulGrupului(g)}</span>
              </span>
              <span className="nr">{g.membri.length}</span>
            </button>
          </li>
        ))}
      </ul>
      {grupAles && (
        <p className="ob-es-nota">{t("struct.editor.grup_selectat", { n: grupAles.membri.length })}</p>
      )}
    </div>
  );
  /** membrii grupului ales, selectați pe plan ca elementul ales */
  const inGrup = new Set(grupAles?.membri ?? []);
  const grupVazut = new Set(grupAprins ?? []);
  /** pe cine se scrie o secțiune: elementul, sau toată clasa lui când omul
   *  a ales «Toată clasa» și clasa are mai mulți membri */
  const tintele = (id: string): string[] =>
    peClasa && clasaAleasa && clasaAleasa.membri.length > 1 ? clasaAleasa.membri : [id];
  /** selectorul «Doar elementul | Toată clasa» și nota lui, pentru panou */
  const selectorClasa = (c: Clasa | null, id: string) => c && (
    <>
      <div className="seg ob-es-clasa-seg" title={t("struct.editor.clasa_domeniu_titlu")}>
        <button className={!peClasa || c.membri.length < 2 ? "on" : ""} disabled={doarCitire}
                onClick={() => setPeClasa(false)}>{t("struct.editor.clasa_doar_el")}</button>
        <button className={peClasa && c.membri.length > 1 ? "on" : ""}
                disabled={doarCitire || c.membri.length < 2}
                onClick={() => setPeClasa(true)}>
          {t("struct.editor.clasa_toata", { n: c.membri.length })}
        </button>
      </div>
      {c.membri.length > 1 && (
        <p className="ob-es-nota">
          {peClasa ? t("struct.editor.clasa_nota_toata", { n: c.membri.length, c: c.eticheta })
            : t("struct.editor.clasa_nota_el", { id, c: c.eticheta })}
        </p>
      )}
    </>
  );
  const cipClasa = (c: Clasa | null) => c && (
    <span className="ob-es-clasa-cip"
          title={t("struct.editor.clasa_cip_titlu")}
          onMouseEnter={() => setClasaAprinsa(c.cheie)} onMouseLeave={() => setClasaAprinsa(null)}>
      {c.membri.length > 1
        ? t("struct.editor.clasa_cip", { c: c.eticheta, n: c.membri.length })
        : t("struct.editor.clasa_singur", { c: c.eticheta })}
    </span>
  );
  const cote = esvAles && sectiunea(esvAles, nivelEf) && !doarCitire
    ? coteleEsv(cadru, G, esvAles, nivelEf, axe, 26 / k) : [];
  const deGrinda = grindaDe ? G.esv.find((x) => x.id === grindaDe.id) ?? null : null;
  /** cu unealta grindă: elementul și piesa de sub cursor și, cu primul capăt
   *  dat, capetele grinzii care s-ar face spre ele (`capeteleGrinziiNoi`) */
  const tintaSubCursor = (() => {
    if (unealta !== "grinda" || doarCitire || !cursor) return null;
    const pm = laPlansa(cadru, cursor);
    const sub = esvSubPunct(G, pm, nivelEf, tol * 1000);
    if (!sub) return null;
    const sec = sectiuneLipita ?? sectiuneaObisnuita(G, nivelEf);
    const capete = grindaDe && deGrinda && deGrinda.id !== sub.esv.id
      ? capeteleGrinziiNoi(deGrinda, grindaDe, sub.esv, sub.piesaIdx, nivelEf, pm, tol * 1000, axeArh,
                           altApasat, 10 * sec.b_cm)
      : null;
    return { esv: sub.esv, piesa: sub.piesa, capete };
  })();
  /** cu Alt: punctele de ancorare ale elementului de sub cursor, ca înainte */
  const ancoraSubCursor: Ancorare | null = tintaSubCursor && (altApasat || grindaDe?.p) && cursor
    ? ancorareaSubPunct(G, laPlansa(cadru, cursor), nivelEf, tol * 1000, grindaDe?.p ?? null, axeArh)
    : null;
  /** dreptunghiul unei grinzi între două capete, pe ecran */
  const dreptunghiulGrinzii = (a: Pt, b: Pt, b_cm: number): string => {
    const [ax, ay] = laEcran(cadru, a);
    const [bx, by] = laEcran(cadru, b);
    const L = Math.hypot(bx - ax, by - ay) || 1;
    const nx = (-(by - ay) / L) * (b_cm / 200), ny = ((bx - ax) / L) * (b_cm / 200);
    return [[ax + nx, ay + ny], [bx + nx, by + ny], [bx - nx, by - ny], [ax - nx, ay - ny]]
      .map((q) => q.join(",")).join(" ");
  };
  const secAles = esvAles ? sectiunea(esvAles, nivelEf) : undefined;
  // semnele «+»: unde zidurile arhitecturii lasă elementul ales să continue
  const plusuri: Continuare[] = esvAles && secAles && unealta === "alege" && !doarCitire
    ? continuari(G, esvAles, nivelEf, nivelCtx) : [];
  const domeniuNivel = domeniu === "toate" ? null : nivelEf;
  const creste = (c: Continuare) => {
    if (!esvAles) return;
    setPlusPeste(null);
    setCotaScrisa(null);
    schimba(aseazaGrinzile(aplicaContinuarea(G, esvAles.id, nivelEf, domeniuNivel, c),
                           [esvAles.id], ctx, G));
  };
  const forma = esvAles && secAles ? numeleFormei(esvAles, nivelEf) : null;
  const nivEsvAles = esvAles ? nivelurileEsv(esvAles) : [];
  /** calea SVG a unui poligon cu găuri, pe ecran */
  const cale = (inele: Pt[][]) => inele.map((r) => `M${r.map((q) => laEcran(cadru, q).join(" ")).join("L")}Z`).join("");
  /** semnalele ochiurilor nivelului, pentru lista din meniu */
  const semnale = vazute.ochiuri.flatMap((o) => (o.server ? semnaleleOchiului(o.server) : [])
    .map((s) => ({ o, s })));
  const consoleAles = ochiAles?.server
    ? (placaNiv?.console ?? []).filter((c) => ochiAles.server!.console.includes(c.id)) : [];
  const desenCursor: Pt | null = unealta === "placa" && desen?.length && cursor
    ? lipesteColt(laPlansa(cadru, cursor), [desen[desen.length - 1], desen[0], ...repere(laPlansa(cadru, cursor))],
                  tolColt).p
    : null;

  const unelte = (
    <div className="card ob-es-unelte">
      {!doarCitire && (
        <>
          {/* «Adaugă», la capătul stâng al barei, despărțit de selectorul de
              niveluri: selecția e starea implicită, fără buton; apăsat, deschide
              cele trei feluri de creat într-un dropdown; cât e o unealtă
              activă, el o arată și o închide la a doua apăsare (sau Esc) */}
          <div className="ob-es-creare">
            <button className={`btn ob-es-plus-buton${creare || unealta !== "alege" ? " on" : ""}`}
                    title={t("struct.editor.creare_titlu")} aria-haspopup="menu" aria-expanded={creare}
                    onClick={() => {
                      if (unealta !== "alege") { setUnealta("alege"); setGrindaDe(null); setDesen(null); setGhidPlaca([]); }
                      else setCreare(!creare);
                    }}>
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
              <span>{unealta === "alege" ? t("struct.editor.adauga") : t(`struct.editor.unealta_${unealta}` as Cheie)}</span>
              {unealta === "alege" && (
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                </svg>
              )}
            </button>
            {creare && (
              <div className="card ob-es-creare-meniu" role="menu">
                {([["esv", "C"], ["grinda", "B"], ["placa", "P"]] as const).map(([u, tasta]) => (
                  <button key={u} role="menuitem" className={unealta === u ? "on" : ""}
                          title={t(`struct.editor.unealta_${u}_titlu` as Cheie)}
                          onClick={() => {
                            setUnealta(u); setCreare(false); setGrindaDe(null); setGrindaSub(null); setDesen(null);
                            setGhidPlaca([]); setConsolaEd(null);
                          }}>
                    <span>{t(`struct.editor.unealta_${u}` as Cheie)}</span><kbd>{tasta}</kbd>
                  </button>
                ))}
              </div>
            )}
            {unealta === "grinda" && sectiuneLipita && (
              <span className="ob-es-unealta-cip">
                {t("struct.editor.grinda_cip_copie", {
                  s: `${sectiuneLipita.b_cm}×${Math.round(sectiuneLipita.h_m * 100)}` })}
              </span>
            )}
            {unealta !== "alege" && !sectiuneLipita && <span className="ob-es-unealta-cip">Esc</span>}
          </div>
          <span className="ob-es-sep ob-es-sep-lat" />
          <div className="seg" title={t("struct.editor.domeniu_titlu")}>
            <button className={domeniu === "nivel" ? "on" : ""}
                    onClick={() => setDomeniu("nivel")}>{t("struct.editor.domeniu_nivel")}</button>
            <button className={domeniu === "toate" ? "on" : ""}
                    onClick={() => setDomeniu("toate")}>{t("struct.editor.domeniu_toate")}</button>
          </div>
          {/* anularea și refacerea stau numai pe ⌘Z / ⇧⌘Z, fără butoane */}
        </>
      )}
      {/* starea verificării pe server: un punct, cu textul în tooltip — scris
          numai când verificarea n-a mers, ca să nu stea un cuvânt permanent
          lângă unelte */}
      {doarCitire ? (
        <span className="ob-es-salvare">{t("struct.editor.doar_citire")}</span>
      ) : (
        <span className={`ob-es-verif ${verificare}${toateGreselile.length ? " greseli" : ""}`}
              title={[t(`struct.editor.verif_${verificare}` as Cheie),
                      ...(toateGreselile.length ? [t("struct.editor.greseli", { n: toateGreselile.length }), ...toateGreselile] : []),
                      ...(iesiri.size ? [t(verdicte[corpIdx] ? "struct.editor.iesiri_verdict" : "struct.editor.iesiri",
                                           { n: iesiri.size, nivel: nivelCtx?.nivel ?? "" })] : [])].join("\n")}>
          <i />{verificare === "eroare" && t("struct.editor.verif_eroare")}
        </span>
      )}
    </div>
  );

  return (
    <div className="ob-studio-wrap ob-es">
      <aside className="card bara-vederi meniu-stanga ob-studio-meniu">
        {sus}
        {editare.corpuri.length > 1 && (
          <span className="grup-vederi">
            <span className="lbl">{t("viz.grup.corp")}</span>
            <select className="inp meniu-select" value={corpIdx}
                    onChange={(e) => { setCorpIdx(Number(e.target.value)); setAles(null);
                                       setNivel(null); setGrindaDe(null); setConsolaEd(null); setDesen(null); }}>
              {editare.corpuri.map((c, i) => (
                <option key={i} value={i} disabled={!!c.indisponibil}
                        title={c.indisponibil ?? undefined}>
                  {c.indisponibil ? t("struct.editor.corp_indisponibil", { cod: c.cod ?? "" }) : c.cod}
                </option>
              ))}
            </select>
          </span>
        )}
        <span className="grup-vederi">
          <span className="lbl">{t("viz.grup.nivel")}</span>
          <div className="ob-es-niveluri">
            {[...niveluri].reverse().map((n) => (
              <button key={n.index} className={n.index === nivelEf ? "on" : ""}
                      onClick={() => { setNivel(n.index); setGrindaDe(null); setCotaScrisa(null);
                                       setConsolaEd(null); setDesen(null); if (ales?.fel === "ochi" || ales?.fel === "gol") setAles(null); }}>
                <span>{n.nivel}</span>
                <span className="nr" title={t("struct.editor.legenda_nr")}>
                  {G.esv.filter((e) => sectiunea(e, n.index)).length}
                  {" · "}
                  {G.grinzi.filter((g) => g.sectiune_per_nivel[String(n.index)]).length}
                  {" · "}
                  {placiCorp.find((p) => p.nivel === n.index)?.ochiuri.length ?? "—"}
                </span>
              </button>
            ))}
          </div>
        </span>
        <span className="grup-vederi">
          <span className="lbl">{t("struct.editor.straturi")}</span>
          <div className="ob-es-straturi">
            {([["ochiuri", PAL.placa], ["esv", PAL.stalp], ["grinzi", PAL.grinda], ["pereti", "#cfd3d8"],
               ["zidarie", PAL.zidarieConfinata], ["etichete", "#1f2328"]] as const)
              .filter(([s]) => s !== "zidarie" || (G.zidarie_confinata ?? []).length > 0)
              .map(([s, cul]) => {
              const fortat = !straturi[s] && vede(s);
              return (
                <button key={s} className={`ob-es-strat${straturi[s] ? " on" : ""}${fortat ? " fortat" : ""}`}
                        aria-pressed={straturi[s]}
                        title={fortat ? t("struct.editor.strat_fortat") : undefined}
                        onClick={() => comutaStratul(s)}>
                  <i style={{ background: cul }} />{t(`struct.editor.strat_${s}` as Cheie)}
                </button>
              );
            })}
          </div>
        </span>
        {vede("ochiuri") && semnale.length > 0 && (
          <span className="grup-vederi">
            <span className="lbl">{t("struct.editor.semnale_ochiuri", { n: semnale.length })}</span>
            <ul className="ob-es-semnale">
              {semnale.slice(0, 12).map(({ o, s }, i) => (
                <li key={i}>
                  <button className={ochiAles === o ? "on" : ""}
                          onClick={() => { setUnealta("alege"); setConsolaEd(null);
                                           setAles({ fel: "ochi", p: interior(o.poli) }); }}>
                    <b>{o.id}</b> {textulSemnalului(t, s, o.server!)}
                  </button>
                </li>
              ))}
              {semnale.length > 12 && <li className="sg-muted">…</li>}
            </ul>
          </span>
        )}
        {/* D8: tipurile sunt libere în editor, dar se VĂD; peste 5 se
            avertizează, fără să se blocheze nimic */}
        <span className="grup-vederi">
          <span className="lbl" title={t("struct.editor.clase_titlu")}>{t("struct.editor.clase")}</span>
          <ul className="ob-es-clase">
            {claseNivel.map((c) => (
              <li key={c.cheie}>
                <button className={clasaVazuta === c.cheie ? "on" : ""}
                        onMouseEnter={() => setClasaAprinsa(c.cheie)}
                        onMouseLeave={() => setClasaAprinsa(null)}
                        onClick={() => { setUnealta("alege"); setConsolaEd(null);
                                         setAles({ fel: c.familie, id: c.membri[0] }); }}>
                  <i style={{ background: c.familie === "esv" ? PAL.stalp : PAL.grinda }} />
                  <span>{c.eticheta}</span>
                  <span className="nr">{c.membri.length}</span>
                </button>
              </li>
            ))}
          </ul>
          <span className={`ob-es-tipuri${(tipuriEsv[corpIdx] ?? 0) > 5 ? " peste" : ""}`}
                title={t("struct.editor.tipuri_esv_titlu")}>
            {t("struct.editor.tipuri_esv", { n: tipuriEsv[corpIdx] ?? "—" })}
          </span>
        </span>
        <span className="grup-vederi">
          {areAxe && (
            <button className={`ob-es-calc-buton${arataAxe ? " on" : ""}`}
                    title={t(axeDerivate ? "struct.editor.axe_derivate_titlu" : "struct.editor.axe_arh_titlu")}
                    onClick={() => setArataAxe(!arataAxe)}>
              {t(axeDerivate
                ? (arataAxe ? "struct.editor.axe_derivate_ascunde" : "struct.editor.axe_derivate_arata")
                : (arataAxe ? "struct.editor.axe_arh_ascunde" : "struct.editor.axe_arh_arata"))}
            </button>
          )}
          <span className="lbl">{t("struct.editor.calc_arh")}</span>
          <div className="ob-es-calc-rand">
            <button className={`ob-es-calc-buton${calc.vizibil ? " on" : ""}`}
                    onClick={() => setCalc({ ...calc, vizibil: !calc.vizibil })}>
              {t(calc.vizibil ? "struct.editor.calc_arh_ascunde" : "struct.editor.calc_arh_arata")}
            </button>
            <input type="range" min={10} max={100} step={5}
                   value={Math.round(calc.opacitate * 100)}
                   title={t("struct.editor.calc_arh_opacitate")}
                   onChange={(e) => setCalc({ vizibil: true, opacitate: Number(e.target.value) / 100 })} />
            <span className="nr">{Math.round(calc.opacitate * 100)} %</span>
          </div>
          {docStare === "se_incarca" && (
            <span className="ob-es-calc-nota">{t("struct.editor.calc_arh_se_incarca")}</span>
          )}
          {docStare === "eroare" && (
            <span className="ob-es-calc-nota rau">{t("struct.editor.calc_arh_lipsa")}</span>
          )}
          {calc.vizibil && starePlansa === "se_incarca" && (
            <span className="ob-es-calc-nota">{t("struct.editor.calc_plansa_se_incarca")}</span>
          )}
          {calc.vizibil && starePlansa === "eroare" && (
            <span className="ob-es-calc-nota rau">{t("struct.editor.calc_plansa_lipsa")}</span>
          )}
          {calc.vizibil && plansaStearsa && (
            <span className="ob-es-calc-nota rau">
              {t("struct.editor.calc_plansa_stearsa",
                 { fisier: plansaStearsa.fisier ?? plansaStearsa.fisier_id })}
            </span>
          )}
          {calc.vizibil && docArh && !cuPlansa && planseeCalc.length === 0 && (
            <span className="ob-es-calc-nota">{t("struct.editor.calc_arh_fara_nivel")}</span>
          )}
        </span>
      </aside>

      <div className="ob-studio ob-es-panza" ref={gazda}
           style={{ cursor: spatiu ? "grab" : unealta === "alege" || doarCitire ? "default" : "crosshair" }}
           onPointerDown={pePanza} onPointerMove={miscare} onPointerUp={ridicare}
           onPointerLeave={() => { setCursor(null); setGrindaSub(null); setCapTras(null);
                                  if (!gest.current) setLipire(null); }}
           onContextMenu={(e) => deschideMeniul(e, null)}>
        {view && (
          <svg width="100%" height="100%" className="ob-es-svg">
            {/* hașurile plăcii, în metri (pânza e scalată pe metru) */}
            <defs>
              <pattern id="ob-es-hasura-consola" width={0.35} height={0.35} patternUnits="userSpaceOnUse"
                       patternTransform="rotate(45)">
                <line x1={0} y1={0} x2={0} y2={0.35} stroke="#7d94ab" strokeWidth={0.04} />
              </pattern>
              <pattern id="ob-es-hasura-gol" width={0.5} height={0.5} patternUnits="userSpaceOnUse"
                       patternTransform="rotate(-45)">
                <rect width={0.5} height={0.5} fill="#fff" />
                <line x1={0} y1={0} x2={0} y2={0.5} stroke="#b9c0c8" strokeWidth={0.035} />
              </pattern>
            </defs>
            <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
              {/* pereții și golurile nivelului */}
              {vede("pereti") && nivelCtx?.pereti.map((p, i) => (
                <polygon key={`p${i}`} points={poli(p.colturi)}
                         className={p.zid === false ? "ob-es-perete-sarcina" : "ob-es-perete"}>
                  {p.motiv && <title>{`${p.id ?? ""}: ${p.motiv}`}</title>}
                </polygon>
              ))}
              {vede("pereti") && nivelCtx?.goluri.map((g, i) => (
                <polygon key={`g${i}`} points={poli(g.colturi)} className="ob-es-gol" />
              ))}
              {/* zidăria confinată a nivelului (proeminența): e STRUCTURĂ, nu
                  perete de arhitectură — plină, în culoarea ei, cu stâlpișorii
                  zidului (cei fără element de beton dedesubt) */}
              {vede("zidarie") && (G.zidarie_confinata ?? []).filter((z) => z.nivel === nivelEf).map((z) => {
                const gros = z.grosime_mm / 2;
                const pts: Pt[] = z.axa === "X"
                  ? [[z.de_la_mm, z.cota_mm - gros], [z.pana_la_mm, z.cota_mm - gros],
                     [z.pana_la_mm, z.cota_mm + gros], [z.de_la_mm, z.cota_mm + gros]]
                  : [[z.cota_mm - gros, z.de_la_mm], [z.cota_mm + gros, z.de_la_mm],
                     [z.cota_mm + gros, z.pana_la_mm], [z.cota_mm - gros, z.pana_la_mm]];
                const stalpisori = (z.stalpisori ?? []).filter((st) => !st.esv);
                const l = 150;   // stâlpișorul zidului: 30 × 30, centrat pe ax
                return (
                  <g key={z.id} className="ob-es-zc">
                    <polygon points={poli(pts)} className="ob-es-zc-perete">
                      <title>{t("struct.editor.zc_titlu", {
                        id: z.id, cm: Math.round(z.grosime_mm / 10), n: (z.stalpisori ?? []).length })}</title>
                    </polygon>
                    {stalpisori.map((st, i) => {
                      const c: Pt = z.axa === "X" ? [st.la_mm, z.cota_mm] : [z.cota_mm, st.la_mm];
                      return (
                        <polygon key={i} className="ob-es-zc-stalpisor"
                                 points={poli([[c[0] - l, c[1] - l], [c[0] + l, c[1] - l],
                                               [c[0] + l, c[1] + l], [c[0] - l, c[1] + l]])} />
                      );
                    })}
                  </g>
                );
              })}
              {/* axele arhitectului, cu bulina cu numele la capăt */}
              {arataAxe && (nivelCtx?.axe ?? []).map((a, i) => {
                const [xa, ya] = laEcran(cadru!, a.a);
                const [xb, yb] = laEcran(cadru!, a.b);
                return (
                  <g key={`ax${i}`} className={`ob-es-axa${a.derivata ? " derivata" : ""}`}
                     pointerEvents="none">
                    <line x1={xa} y1={ya} x2={xb} y2={yb} />
                    {a.nume && (
                      <>
                        <circle cx={xa} cy={ya} r={0.35} />
                        <text x={xa} y={ya} dy="0.12">{a.nume}</text>
                      </>
                    )}
                  </g>
                );
              })}
              {/* PLACA de la capul nivelului: ochiurile FEM-ului (sub
                  grinzi și ESV), marginile fără reazem, consolele, golurile
                  scoase de om și balcoanele rămase fără tavan */}
              {vede("ochiuri") && placaNiv && (
                <g className="ob-es-placa">
                  {vazute.ochiuri.map((o, i) => (
                    <path key={`o${o.id ?? `p${i}`}`} d={cale(o.poli)}
                          className={`ob-es-ochi n${nuante[i]}${o.server ? "" : " previz"}${
                            ochiAles === o ? " ales" : ""}`}
                          pointerEvents={unealta === "alege" ? "all" : "none"}
                          onPointerDown={(e) => peOchi(e, o)}>
                      {o.id && <title>{`${o.id} · ${o.arie_m2.toFixed(1).replace(".", ",")} m²`}</title>}
                    </path>
                  ))}
                  {placaNiv.console.map((c) => (
                    <polygon key={`c${c.id}`} points={poli(c.contur)} className="ob-es-consola" />
                  ))}
                  {vazute.ochiuri.flatMap((o, i) => (o.server?.laturi ?? [])
                    .filter((l) => l.reazem === "contur_fara_reazem")
                    .map((l, j) => {
                      const [ax, ay] = laEcran(cadru, l.a);
                      const [bx, by] = laEcran(cadru, l.b);
                      return <line key={`l${i}.${j}`} x1={ax} y1={ay} x2={bx} y2={by}
                                   className="ob-es-fara-reazem" />;
                    }))}
                  {golurileOmului.map((r, i) => (
                    <polygon key={`go${i}`} points={poli(r)}
                             className={`ob-es-placa-scoasa${golAles === r ? " ales" : ""}`}
                             pointerEvents={unealta === "alege" ? "all" : "none"}
                             onPointerDown={(e) => peGol(e, r)} />
                  ))}
                  {!doarCitire && placaNiv.fara_tavan.map((b) => (
                    <polygon key={`ft${b.id}`} points={poli(b.contur)} className="ob-es-fara-tavan"
                             pointerEvents="none" />
                  ))}
                </g>
              )}
              {/* ESV-urile nivelului de dedesubt care se opresc acolo */}
              {vede("esv") && esvDedesubt.map((e) => conturulEsv(e, nivelEf - 1).map((pts, i) => (
                <polygon key={`d${e.id}${i}`} points={poli(pts)} className="ob-es-dedesubt" />
              )))}
              {/* grinzile */}
              {vede("grinzi") && grinziNivel.map((g) => {
                const a = capatulGrinzii(G, g, "i");
                const b = capatulGrinzii(G, g, "j");
                if (!a || !b) return null;
                const [ax, ay] = laEcran(cadru, a);
                const [bx, by] = laEcran(cadru, b);
                const s = g.sectiune_per_nivel[String(nivelEf)];
                const alesa = ales?.fel === "grinda" && ales.id === g.id;
                const deImpartit = unealta === "esv" && grindaSub === g.id;
                const oblica = err.grinzi.has(g.id);
                return (
                  <g key={g.id} onPointerDown={(e) => peGrinda(e, g.id)}
                     onContextMenu={(e) => deschideMeniul(e, { fel: "grinda", id: g.id })}
                     style={{ cursor: "pointer" }}>
                    <line x1={ax} y1={ay} x2={bx} y2={by} stroke="transparent"
                          strokeWidth={Math.max(s.b_cm / 100, 12 / k)} />
                    <line x1={ax} y1={ay} x2={bx} y2={by}
                          stroke={oblica ? "#b5323d" : PAL.grinda}
                          strokeOpacity={0.85} strokeWidth={s.b_cm / 100}
                          strokeDasharray={oblica ? `${6 / k} ${4 / k}` : undefined} />
                    {alesa && (
                      <line x1={ax} y1={ay} x2={bx} y2={by} stroke={SEL} strokeWidth={3}
                            vectorEffect="non-scaling-stroke" />
                    )}
                    {!alesa && clasaVazuta != null && cheiaGrinzii(g, nivelEf) === clasaVazuta && (
                      <line x1={ax} y1={ay} x2={bx} y2={by} className="ob-es-din-clasa" />
                    )}
                    {deImpartit && (
                      <line x1={ax} y1={ay} x2={bx} y2={by} className="ob-es-de-impartit" />
                    )}
                    {k > 26 && straturi.etichete && (
                      <text x={(ax + bx) / 2} y={(ay + by) / 2} className="ob-es-eticheta grinda"
                            fontSize={10 / k} dy={-(s.b_cm / 200 + 4 / k)}
                            transform={Math.abs(bx - ax) < Math.abs(by - ay)
                              ? `rotate(-90 ${(ax + bx) / 2} ${(ay + by) / 2})` : undefined}>
                        {g.id} · {s.b_cm}×{Math.round(s.h_m * 100)}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* ESV-urile nivelului */}
              {vede("esv") && esvNivel.map((e) => {
                const alesE = esvAles?.id === e.id;
                // de la capătul ales pleacă o grindă dreaptă spre elementul ăsta:
                // pe o linie comună a pieselor, sau pe linia punctului fixat
                const candidat = !!grindaDe && !!deGrinda && grindaDe.id !== e.id
                  && (grindaDe.p
                    ? puncteDeAncorare(e, nivelEf, grindaDe.p).some((c) => c.fel === "linie")
                    : liniileIntre(deGrinda, e, nivelEf, axeArh, grindaDe.piesa, null, null,
                                   10 * (sectiuneLipita ?? sectiuneaObisnuita(G, nivelEf)).b_cm).length > 0);
                const s = sectiunea(e, nivelEf)!;
                const cut = cutiaEsv(cadru, e, nivelEf);
                return (
                  <g key={e.id} onPointerDown={(ev) => peEsv(ev, e.id)}
                     onContextMenu={(ev) => deschideMeniul(ev, { fel: "esv", id: e.id })}
                     style={{ cursor: doarCitire ? "pointer" : unealta === "alege" ? "move" : "pointer" }}>
                    {/* piesele: piciorul cu colțurile lui și brațele pornite din
                        fața lui, fără suprapuneri */}
                    {conturulEsv(e, nivelEf).map((pts, i) => (
                      <polygon key={i} points={poli(pts)}
                               className={`ob-es-esv${alesE ? " ales" : ""}${
                                 deGrinda?.id === e.id ? " de-grinda" : ""}${candidat ? " candidat" : ""}${
                                 iesiri.has(e.id) ? " iese" : ""}${
                                 !alesE && clasaVazuta != null && cheiaEsv(e, nivelEf) === clasaVazuta
                                   ? " din-clasa" : ""}${!alesE && simetric && gemeniAles.has(e.id) ? " geaman" : ""}${
                                 !alesE && inGrup.has(e.id) ? " in-grup" : ""}${
                                 !alesE && !inGrup.has(e.id) && grupVazut.has(e.id) ? " grup-aprins" : ""}`}
                               style={{ fill: PAL.stalp }} />
                    ))}
                    {iesiri.get(e.id)?.bucati.map((pts, i) => (
                      <polygon key={`i${i}`} points={poli(pts)} className="ob-es-iesire" />
                    ))}
                    {alesE && (() => {
                      const [nx, ny] = laEcran(cadru, e.nod);
                      const r = 5 / k;
                      return (
                        <g className="ob-es-nod">
                          <line x1={nx - r} y1={ny} x2={nx + r} y2={ny} />
                          <line x1={nx} y1={ny - r} x2={nx} y2={ny + r} />
                        </g>
                      );
                    })()}
                    {k > 22 && cut && straturi.etichete && (
                      <text x={(cut.x[0] + cut.x[1]) / 2} y={cut.y[1] + 11 / k}
                            className="ob-es-eticheta" fontSize={10 / k}>
                        {e.id} · {s.b_cm}×{s.L_cm}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* capetele libere ale pieselor: se trag de pe orice element;
                  mânerele se văd pe cel ales și pe cel tras */}
              {unealta === "alege" && !doarCitire && vede("esv") && esvNivel.map((e) => {
                const ps = piesele(e, nivelEf);
                if (!ps) return null;
                const alesE = esvAles?.id === e.id;
                return ps.capete.map((c) => {
                  const lungPx = (lungimeaPiesei(c) / 1000) * k;
                  const tras = tragere?.id === e.id && tragere.c.brat === c.brat
                    && tragere.c.sens === c.sens;
                  if (lungPx < PIESA_MIN_PX && !tras) return null;
                  const pe = (v: number, w: number): Pt => laEcran(cadru, c.axa === 0 ? [v, w] : [w, v]);
                  const a = pe(c.poz, c.peste[0]);
                  const b = pe(c.poz, c.peste[1]);
                  const span = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
                  const [ux, uy] = [(b[0] - a[0]) / span, (b[1] - a[1]) / span];
                  const m: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
                  const g = Math.min(span * 0.35, 8 / k);
                  return (
                    <g key={`cap${e.id}:${c.brat == null ? "p" : c.la_mm}:${c.sens}`}
                       className={`ob-es-trage${tras ? " tras" : ""}`}
                       style={{ cursor: c.axa === 0 ? "ew-resize" : "ns-resize" }}
                       onPointerDown={(ev) => peCapatLiber(ev, e.id, c)}>
                      <title>{t(c.brat == null ? "struct.editor.trage_picior" : "struct.editor.trage_brat")}</title>
                      <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className="ob-es-trage-zona"
                            strokeWidth={Math.max(6, Math.min(14, lungPx / 2)) / k} />
                      {(alesE || tras) && (
                        <>
                          <line x1={m[0] - ux * g} y1={m[1] - uy * g} x2={m[0] + ux * g} y2={m[1] + uy * g}
                                className="ob-es-trage-halo" />
                          <line x1={m[0] - ux * g} y1={m[1] - uy * g} x2={m[0] + ux * g} y2={m[1] + uy * g}
                                className="ob-es-trage-maner" />
                        </>
                      )}
                    </g>
                  );
                });
              })}
              {/* fața trasă: vecinul în care s-a oprit (lăsată acolo, se unesc),
                  linia de care s-a lipit și lungimea brațului */}
              {tragere && (() => {
                const c = tragere.c;
                const vecin = tragere.vecin ? G.esv.find((x) => x.id === tragere.vecin) : null;
                const pe = (v: number, w: number): Pt => laEcran(cadru, c.axa === 0 ? [v, w] : [w, v]);
                const gh = tragere.ghidaj != null ? pe(tragere.ghidaj, 0) : null;
                const lung = lungimeaPiesei(c, tragere.poz);
                // lângă latura de sus / din dreapta a brațului, în afara lui
                const dep = (26 / k) * 1000;
                return (
                  <>
                    {vecin && conturulEsv(vecin, nivelEf).map((pts, i) => (
                      <polygon key={`v${i}`} points={poli(pts)} className="ob-es-tinta" />
                    ))}
                    {gh && (c.axa === 0
                      ? <line x1={gh[0]} x2={gh[0]} y1={-1e4} y2={1e4} className="ob-es-ghidaj" />
                      : <line y1={gh[1]} y2={gh[1]} x1={-1e4} x2={1e4} className="ob-es-ghidaj" />)}
                    {c.brat != null && lung > 0 && (
                      <CotaDesen c={{ a: pe(c.fix, c.peste[1] + dep), b: pe(tragere.poz, c.peste[1] + dep),
                                      valoare: lung / 1000 }}
                                 k={k} culoare={COTA_NEGRU} corp={COTA_CORP} />
                    )}
                  </>
                );
              })()}
              {/* etichetele ochiurilor, deasupra grinzilor */}
              {vede("ochiuri") && straturi.etichete && vazute.ochiuri.map((o, i) => {
                const [x0, y0, x1, y1] = cutie(o.poli[0]);
                const ales_ = ochiAles === o;
                if (Math.min(x1 - x0, y1 - y0) / 1000 * k < 46 && !ales_) return null;
                const [x, y] = laEcran(cadru, interior(o.poli));
                return (
                  <g key={`et${o.id ?? i}`} className="ob-es-ochi-et">
                    <text x={x} y={y - 7 / k} fontSize={11 / k} className="id">{o.id ?? "…"}</text>
                    <text x={x} y={y + 7 / k} fontSize={10 / k}>
                      {`${o.arie_m2.toFixed(1).replace(".", ",")} m²`}
                    </text>
                  </g>
                );
              })}
              {/* balconul fără tavan: un clic îl acoperă cu placă */}
              {vede("ochiuri") && placaNiv && !doarCitire && unealta === "alege"
                && placaNiv.fara_tavan.map((b) => {
                  const [x, y] = laEcran(cadru, interior([b.contur]));
                  const w = 88 / k, h = 20 / k;
                  return (
                    <g key={`tv${b.id}`} className="ob-es-tavan"
                       onPointerDown={(e) => {
                         if (e.button !== 0) return;
                         e.stopPropagation();
                         schimba(adaugaPlaca(G, nivelEf, b.contur));
                         setMesaj(t("struct.editor.tavan_pus", { id: b.id }));
                       }}>
                      <title>{t("struct.editor.tavan_titlu", { id: b.id, m2: b.arie_m2.toFixed(1).replace(".", ",") })}</title>
                      <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={h / 2} />
                      <text x={x} y={y} fontSize={11 / k}>{t("struct.editor.tavan_plus", { id: b.id })}</text>
                    </g>
                  );
                })}
              {/* conturul consolei în lucru: colțurile se trag, «+» pe mijlocul
                  laturii adaugă un colț, lungimile laturilor scrise lângă ele */}
              {consolaEd && (() => {
                const r = consolaEd.cur;
                const scr = r.map((q) => laEcran(cadru, q));
                return (
                  <g className="ob-es-contur-ed">
                    <polygon points={scr.map((q) => q.join(",")).join(" ")} className="linie" />
                    {scr.map((a, i) => {
                      const b = scr[(i + 1) % scr.length];
                      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
                      if (L * k < 34) return null;
                      const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
                      const nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
                      const afara = inPoligon(laPlansa(cadru, [mx + nx * 0.05, my + ny * 0.05]), [r]) ? -1 : 1;
                      const tx = mx + nx * afara * (14 / k), ty = my + ny * afara * (14 / k);
                      const rr = 6 / k;
                      return (
                        <g key={`m${i}`}>
                          <text x={tx} y={ty} fontSize={11 / k} className="cota"
                                transform={Math.abs(b[0] - a[0]) < Math.abs(b[1] - a[1])
                                  ? `rotate(-90 ${tx} ${ty})` : undefined}>
                            {L.toFixed(2).replace(".", ",")}
                          </text>
                          {!doarCitire && (
                            <g className="mijloc" onPointerDown={(e) => peMijloc(e, i)}>
                              <title>{t("struct.editor.placa_colt_nou")}</title>
                              <circle cx={mx} cy={my} r={rr} />
                              <path d={`M${mx - rr * 0.55} ${my}H${mx + rr * 0.55}M${mx} ${my - rr * 0.55}V${my + rr * 0.55}`} />
                            </g>
                          )}
                        </g>
                      );
                    })}
                    {!doarCitire && scr.map(([x, y], i) => (
                      <rect key={`c${i}`} x={x - 4.5 / k} y={y - 4.5 / k} width={9 / k} height={9 / k}
                            className={`colt${coltAles === i ? " on" : ""}`}
                            onPointerDown={(e) => peColt(e, i)}>
                        <title>{t("struct.editor.placa_colt_titlu")}</title>
                      </rect>
                    ))}
                  </g>
                );
              })()}
              {/* placa desenată de la zero, colț cu colț */}
              {desen && desen.length > 0 && (() => {
                const pts = desenCursor ? [...desen, desenCursor] : desen;
                const scr = pts.map((q) => laEcran(cadru, q));
                return (
                  <g className="ob-es-desen-placa">
                    <polygon points={scr.map((q) => q.join(",")).join(" ")} />
                    {desen.map((q, i) => {
                      const [x, y] = laEcran(cadru, q);
                      return <rect key={i} x={x - 4.5 / k} y={y - 4.5 / k} width={9 / k} height={9 / k}
                                   className={i === 0 ? "primul" : ""} />;
                    })}
                  </g>
                );
              })()}
              {ghidPlaca.map((gh, i) => {
                const v = laEcran(cadru, gh.axa === 0 ? [gh.valoare, 0] : [0, gh.valoare]);
                return gh.axa === 0
                  ? <line key={`gp${i}`} x1={v[0]} x2={v[0]} y1={-1e4} y2={1e4} className="ob-es-ghidaj" />
                  : <line key={`gp${i}`} y1={v[1]} y2={v[1]} x1={-1e4} x2={1e4} className="ob-es-ghidaj" />;
              })}
              {/* ghidajele lipirii */}
              {lipire?.ghidaje.map((gh, i) => (
                gh.axa === 0
                  ? <line key={`gh${i}`} x1={gh.valoare} x2={gh.valoare} y1={-1e4} y2={1e4}
                          className="ob-es-ghidaj" />
                  : <line key={`gh${i}`} y1={gh.valoare} y2={gh.valoare} x1={-1e4} x2={1e4}
                          className="ob-es-ghidaj" />
              ))}
              {lipire?.perete && (
                lipire.perete.axa === "X"
                  ? <line x1={lipire.perete.de} x2={lipire.perete.la} y1={lipire.perete.cota}
                          y2={lipire.perete.cota} className="ob-es-ax-zid" />
                  : <line y1={lipire.perete.de} y2={lipire.perete.la} x1={lipire.perete.cota}
                          x2={lipire.perete.cota} className="ob-es-ax-zid" />
              )}
              {/* ESV-ul care s-ar pune */}
              {unealta === "esv" && !doarCitire && lipire && !gest.current && (() => {
                const ax = lipire.perete?.axa ?? "X";
                const b = lipire.perete ? Math.max(0.3, lipire.perete.grosime) : 0.4;
                const L = lipire.perete ? 0.6 : 0.4;
                const [w, h] = ax === "X" ? [L, b] : [b, L];
                return <rect x={lipire.p[0] - w / 2} y={lipire.p[1] - h / 2} width={w} height={h}
                             className="ob-es-fantoma" />;
              })()}
              {/* capătul ales al grinzii de tras și, pe elementul de sub cursor,
                  punctele în care s-ar prinde celălalt capăt */}
              {/* unealta grindă: piesa de plecare (sau punctul fixat cu Alt), piesa
                  de sub cursor și fantoma grinzii care s-ar face între ele — pe
                  linia comună, dreaptă; roșie când n-au niciuna */}
              {grindaDe && deGrinda && (() => {
                const piesaDe = grindaDe.piesa != null ? amprentaEsv(deGrinda, nivelEf)[grindaDe.piesa] : null;
                const cap = tintaSubCursor?.capete ?? null;
                const sec = sectiuneLipita ?? sectiuneaObisnuita(G, nivelEf);
                const dela: Pt | null = grindaDe.p ?? (cap ? cap.a : null);
                const la = cap ? laEcran(cadru, cap.b) : cursor;
                return (
                  <>
                    {piesaDe && !grindaDe.p && (
                      <polygon points={poli(piesaDe)} className="ob-es-piesa-de" />
                    )}
                    {cap && (
                      <polygon points={dreptunghiulGrinzii(cap.a, cap.b, sec.b_cm)}
                               className={`ob-es-fantoma-grinda${cap.linie ? "" : " oblica"}`} />
                    )}
                    {dela && la && !cap && (() => {
                      const [ax, ay] = laEcran(cadru, dela);
                      return <line x1={ax} y1={ay} x2={la[0]} y2={la[1]} className="ob-es-elastic" />;
                    })()}
                    {dela && (() => {
                      const [ax, ay] = laEcran(cadru, dela);
                      return <circle cx={ax} cy={ay} r={5 / k} className="ob-es-ancora aleasa" />;
                    })()}
                    {cap && (() => {
                      const [bx, by] = laEcran(cadru, cap.b);
                      return <circle cx={bx} cy={by} r={5 / k}
                                     className={`ob-es-ancora aleasa${cap.linie ? "" : " libera"}`} />;
                    })()}
                  </>
                );
              })()}
              {tintaSubCursor && tintaSubCursor.esv.id !== grindaDe?.id && (
                <polygon points={poli(tintaSubCursor.piesa)} className="ob-es-tinta" />
              )}
              {ancoraSubCursor && <PuncteAncorare cadru={cadru} k={k} anc={ancoraSubCursor} />}
              {/* capetele grinzii alese: se trag pe alt element */}
              {grindaAleasa && grindaAleasa.sectiune_per_nivel[String(nivelEf)] && (() => {
                const r = 6 / k;
                return (["i", "j"] as const).map((cap) => {
                  const c = capatulGrinzii(G, grindaAleasa, cap);
                  if (!c) return null;
                  const [x, y] = laEcran(cadru, c);
                  const tras = capTras?.gid === grindaAleasa.id && capTras.cap === cap;
                  return (
                    <circle key={`cap${cap}`} cx={x} cy={y} r={r}
                            className={`ob-es-capat${tras ? " tras" : ""}`}
                            style={{ cursor: doarCitire ? "default" : "grab" }}
                            onPointerDown={(e) => peCapat(e, grindaAleasa.id, cap)}>
                      <title>{t("struct.editor.capat_titlu", {
                        esv: cap === "i" ? grindaAleasa.nod_i : grindaAleasa.nod_j })}</title>
                    </circle>
                  );
                });
              })()}
              {capTras && grindaAleasa && capTras.gid === grindaAleasa.id && (() => {
                const alt = capatulGrinzii(G, grindaAleasa, capTras.cap === "i" ? "j" : "i");
                if (!alt) return null;
                const [ax, ay] = laEcran(cadru, alt);
                const pm = laPlansa(cadru, capTras.p);
                // aceeași socoteală ca la lăsare: fantoma grinzii pe linia comună a
                // piesei de sub cursor cu piesa celuilalt capăt, sau oblică (roșie)
                const r = reancoreazaCapatul(G, grindaAleasa.id, capTras.cap, pm, nivelEf, tol * 1000,
                                             axeArh, altApasat);
                const sub = esvSubPunct(G, pm, nivelEf, tol * 1000);
                const G2 = "G" in r ? r.G : null;
                const nou = G2 ? G2.grinzi.find((x) => x.id === grindaAleasa.id) ?? null : null;
                const a = G2 && nou ? capatulGrinzii(G2, nou, "i") : null;
                const b = G2 && nou ? capatulGrinzii(G2, nou, "j") : null;
                const s = grindaAleasa.sectiune_per_nivel[String(nivelEf)];
                const puncte = sub && altApasat
                  ? ancorareaPeEsv(sub.esv, nivelEf, pm, tol * 1000, alt, axeArh) : null;
                return (
                  <>
                    {sub && "G" in r && <polygon points={poli(sub.piesa)} className="ob-es-tinta" />}
                    {a && b && "G" in r
                      ? <polygon points={dreptunghiulGrinzii(a, b, s.b_cm)}
                                 className={`ob-es-fantoma-grinda${r.oblica ? " oblica" : ""}`} />
                      : <line x1={ax} y1={ay} x2={capTras.p[0]} y2={capTras.p[1]} className="ob-es-elastic" />}
                    {puncte && <PuncteAncorare cadru={cadru} k={k} anc={puncte} />}
                  </>
                );
              })()}
              {coteTronson.map((c, i) => (
                <CotaDesen key={`ct${i}`} c={c} k={k} culoare={COTA_TRONSON} />
              ))}
              {/* cotele de lucru ale ESV-ului ales: negre, scriibile */}
              {cote.map((c, i) => {
                const mx = (c.a[0] + c.b[0]) / 2;
                const my = (c.a[1] + c.b[1]) / 2;
                return (
                  <g key={`c${i}`}>
                    <CotaDesen c={{ a: c.a, b: c.b, valoare: c.valoare }} k={k}
                               culoare={COTA_NEGRU} corp={COTA_CORP} />
                    <circle cx={mx} cy={my} r={Math.max(15 / k, 0.0001)} fill="transparent"
                            strokeWidth={0} pointerEvents="all" style={{ cursor: "text" }}
                            onPointerDown={(ev) => {
                              // fără `preventDefault`, apăsarea mută focusul pe
                              // pagină după ce câmpul s-a deschis, iar blur-ul
                              // îl închide pe loc
                              ev.stopPropagation(); ev.preventDefault(); setCotaScrisa(i);
                            }}
                            onMouseDown={(ev) => ev.preventDefault()} />
                    {cotaScrisa === i && (
                      <foreignObject x={mx} y={my} width={1} height={1} style={{ overflow: "visible" }}>
                        <CotaScrisa k={k} valoare={c.valoare}
                                    laScris={(v) => scrieCota(c, v)}
                                    laRenuntat={() => setCotaScrisa(null)} />
                      </foreignObject>
                    )}
                  </g>
                );
              })}
              {/* ce ar adăuga «+»-ul de sub cursor */}
              {esvAles && plusPeste != null && plusuri[plusPeste] && (
                <polygon points={poli(betonulContinuarii(esvAles, nivelEf, plusuri[plusPeste]))}
                         className="ob-es-fantoma-forma" />
              )}
              {/* «+»: elementul continuă pe zid — mai lung, sau cu un braț */}
              {esvAles && plusuri.map((c, i) => {
                const [x, y] = laEcran(cadru, c.poz);
                // direcția pe ecran: Y-ul planșei e întors
                const dx = c.dir[0], dy = -c.dir[1];
                const r = 8 / k;
                const cx = x + dx * (r + 4 / k), cy = y + dy * (r + 4 / k);
                const m = (c.lung_mm / 1000).toFixed(2).replace(".", ",");
                const titlu = c.fel === "picior" ? t("struct.editor.plus_picior", { m })
                  : c.fel === "brat_nou" ? t("struct.editor.plus_brat_nou", { m })
                  : t("struct.editor.plus_brat", { m });
                return (
                  <g key={`plus${i}`} className={`ob-es-plus${plusPeste === i ? " peste" : ""}`}
                     onPointerEnter={() => setPlusPeste(i)}
                     onPointerLeave={() => setPlusPeste((v) => (v === i ? null : v))}
                     onPointerDown={(ev) => { ev.stopPropagation(); if (ev.button === 0) creste(c); }}>
                    <title>{titlu}</title>
                    <circle cx={cx} cy={cy} r={r} />
                    <line x1={cx - r * 0.5} y1={cy} x2={cx + r * 0.5} y2={cy} />
                    <line x1={cx} y1={cy - r * 0.5} x2={cx} y2={cy + r * 0.5} />
                  </g>
                );
              })}
              {/* hârtia de calc, stinsă și surdă: planșa arhitectului, iar fără
                  ea planul documentului */}
              {calc.vizibil && docArh && importul && plansa && cadru && ctx && cuPlansa && (
                <g className="ob-es-calc-arh" opacity={calc.opacitate} pointerEvents="none">
                  <PlansaArhitect desen={plansa} importul={importul} crs={docArh.crs}
                               thetaGrade={ctx.theta_grade} cadru={cadru} pxPerM={k} />
                </g>
              )}
              {calc.vizibil && docArh && !cuPlansa && planseeCalc.length > 0 && (
                <g className="ob-es-calc-arh" opacity={calc.opacitate} pointerEvents="none"
                   transform={transformareCalc}>
                  {planseeCalc.map((pl) => (
                    <StoreyView key={pl.id} plan={pl} pxPerM={k} hartie={false}
                                conventie="tehnic" />
                  ))}
                </g>
              )}
            </g>
          </svg>
        )}

        {meniu && G && (() => {
          const tinta = meniu.tinta;
          const poate = !!tinta && sePoateLipiPe(G, clipboard, tinta.id);
          const inchide = () => setMeniu(null);
          return (
            <div className="card ob-es-meniu" role="menu"
                 style={{ left: meniu.x, top: meniu.y }}
                 onPointerDown={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
              {tinta && (
                <button onClick={() => { copiaza(tinta.id); inchide(); }}>
                  <span>{t("struct.editor.meniu_copiaza")}</span><kbd>⌘C</kbd>
                </button>
              )}
              <button disabled={!clipboard} onClick={() => { lipesteElementul(meniu.p); inchide(); }}>
                <span>{clipboard ? t("struct.editor.meniu_lipeste", { c: numeleCopiei(clipboard, nivelEf) })
                  : t("struct.editor.meniu_lipeste_gol")}</span><kbd>⌘V</kbd>
              </button>
              {tinta && (
                <>
                  <button onClick={() => { copiaza(tinta.id); inchide(); }}>
                    <span>{t("struct.editor.meniu_copiaza_prop")}</span><kbd>⌘C</kbd>
                  </button>
                  <button disabled={!poate}
                          title={clipboard && !poate ? t("struct.editor.meniu_lipeste_prop_alta") : undefined}
                          onClick={() => { lipesteProp(tinta.id); inchide(); }}>
                    <span>{clipboard ? t("struct.editor.meniu_lipeste_prop", { c: numeleCopiei(clipboard, nivelEf) })
                      : t("struct.editor.meniu_lipeste_prop_gol")}</span><kbd>⇧⌘V</kbd>
                  </button>
                  <hr />
                  {tinta.fel === "esv" && (
                    <button onClick={() => { roteste(); inchide(); }}>
                      <span>{t("struct.editor.roteste")}</span><kbd>R</kbd>
                    </button>
                  )}
                  <button className="rau" onClick={() => { sterge(); inchide(); }}>
                    <span>{tinta.fel === "esv"
                      ? (domeniu === "toate" ? t("struct.editor.sterge_tot") : t("struct.editor.sterge_de_aici"))
                      : (domeniu === "toate" ? t("struct.editor.sterge_grinda_tot") : t("struct.editor.sterge_grinda_nivel"))}
                    </span><kbd>⌫</kbd>
                  </button>
                </>
              )}
            </div>
          );
        })()}
        <div className="float ob-es-sus">{unelte}</div>
        {mesaj && (
          <div className="float ob-es-mesaj">
            <div className="card">
              <span>{mesaj}</span>
              <button className="btn btng" onClick={() => setMesaj(null)}>×</button>
            </div>
          </div>
        )}
        {tragere?.vecin && (
          <div className="float ob-es-indiciu">
            <div className="card">
              {t("struct.editor.unire_indiciu", { a: tragere.id, b: tragere.vecin })}
            </div>
          </div>
        )}
      {/* panoul elementului ales */}
        {(esvAles || grindaAleasa || ochiAles || golAles) && (
          <div className="float ob-es-proprietati"><div className="card">
            {ochiAles && (() => {
              const o = ochiAles.server;
              const nr = (v: number, z = 2) => v.toFixed(z).replace(".", ",");
              return (
                <div className="ob-es-prop">
                  <div className="ob-es-prop-cap">
                    <b>{ochiAles.id ?? t("struct.editor.ochi_nou")}</b>
                    <span className="sg-muted">{t("struct.editor.ochi_peste", { nivel: nivelCtx?.nivel ?? "" })}</span>
                    <Ajutor titlu={t("struct.editor.ajutor")}
                            texte={[t("struct.editor.ochi_nota"),
                                    ...(consoleAles.length && !doarCitire ? [t("struct.editor.consola_ajutor")] : [])]} />
                    <button className="btn btng" onClick={() => setAles(null)}>×</button>
                  </div>
                  <dl className="ob-es-cifre">
                    <dt>{t("struct.editor.ochi_arie")}</dt><dd>{nr(ochiAles.arie_m2)} m²</dd>
                    {o && (
                      <>
                        <dt>{t("struct.editor.ochi_gabarit")}</dt><dd>{nr(o.lx_m)} × {nr(o.ly_m)} m</dd>
                        <dt>{t("struct.editor.ochi_lumina")}</dt><dd>{nr(o.lumina_m)} m</dd>
                        <dt>{t("struct.editor.ochi_deschidere")}</dt><dd>{nr(o.deschidere_m)} m</dd>
                      </>
                    )}
                  </dl>
                  {o ? (
                    <>
                      <div className="ob-es-reazeme" title={t("struct.editor.ochi_reazeme_titlu")}>
                        <i style={{ width: `${o.pe_grinzi_pct}%`, background: PAL.grinda }} />
                        <i style={{ width: `${o.pe_pereti_pct}%`, background: "#9d968f" }} />
                        <i style={{ width: `${o.pe_contur_pct}%`, background: "#b5323d" }} />
                      </div>
                      <div className="ob-es-reazeme-leg">
                        <span><i style={{ background: PAL.grinda }} />{t("struct.editor.ochi_pe_grinzi", { pct: Math.round(o.pe_grinzi_pct) })}</span>
                        <span><i style={{ background: "#9d968f" }} />{t("struct.editor.ochi_pe_pereti", { pct: Math.round(o.pe_pereti_pct) })}</span>
                        <span><i style={{ background: "#b5323d" }} />{t("struct.editor.ochi_fara_reazem", { pct: Math.round(o.pe_contur_pct) })}</span>
                      </div>
                      {semnaleleOchiului(o).map((s) => (
                        <p key={s} className="ob-es-nota ob-es-nota-iese">{textulSemnalului(t, s, o)}</p>
                      ))}
                    </>
                  ) : (
                    <p className="ob-es-nota">{t("struct.editor.ochi_previzualizare")}</p>
                  )}
                  {consoleAles.map((c) => {
                    const ed = consolaEd?.id === c.id;
                    return (
                      <div key={c.id} className="ob-es-consola-card">
                        <div className="cap">
                          <b>{c.sursa === "om" ? t("struct.editor.consola_om") : t("struct.editor.consola_arh", { id: c.id })}</b>
                          <span className="sg-muted">
                            {c.arie_m2 != null && `${nr(c.arie_m2, 1)} m²`}
                            {c.adanc_m != null && ` · ${t("struct.editor.consola_iese", { m: nr(c.adanc_m) })}`}
                          </span>
                        </div>
                        {!doarCitire && (
                          <div className="ob-es-butoane">
                            {ed
                              ? <button className="btn btnp" onClick={() => setConsolaEd(null)}>{t("struct.editor.consola_gata")}</button>
                              : <button className="btn" onClick={() => incepeConturul(c)}>{t("struct.editor.consola_editeaza")}</button>}
                            <button className="btn btnd" onClick={() => scoateConsola(c)}>{t("struct.editor.consola_scoate")}</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!doarCitire && (
                    <div className="ob-es-butoane">
                      <button className="btn btnd" onClick={() => scoateOchiul(ochiAles)}>
                        {peToate ? t("struct.editor.ochi_scoate_tot") : t("struct.editor.ochi_scoate")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
            {golAles && (
              <div className="ob-es-prop">
                <div className="ob-es-prop-cap">
                  <b>{t("struct.editor.gol_titlu")}</b>
                  <span className="sg-muted">
                    {`${(ariaInel(golAles) / 1e6).toFixed(1).replace(".", ",")} m²`}
                  </span>
                  <button className="btn btng" onClick={() => setAles(null)}>×</button>
                </div>
                <p className="ob-es-nota">{t("struct.editor.gol_nota")}</p>
                {!doarCitire && (
                  <div className="ob-es-butoane">
                    <button className="btn btnp" onClick={() => puneGolul(golAles)}>
                      {peToate ? t("struct.editor.gol_pune_tot") : t("struct.editor.gol_pune")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {esvAles && (
              <div className="ob-es-prop">
                <div className="ob-es-prop-cap">
                  <b>{esvAles.id}</b>
                  <span className="sg-muted">{t("struct.editor.esv_axa", { axa: esvAles.axa })}</span>
                  <Ajutor titlu={t("struct.editor.ajutor")}
                          texte={[domeniu === "toate" ? t("struct.editor.aplica_toate") : t("struct.editor.aplica_nivel"),
                                  ...(doarCitire ? [] : [plusuri.length ? t("struct.editor.brate_nota")
                                    : t("struct.editor.brate_nota_fara")])]} />
                  <button className="btn btng" onClick={() => setAles(null)}>×</button>
                </div>
                {cipClasa(clasaAleasa)}
                {cipGemeni}
                {panouGrupuri}
                {secAles ? (
                  <>
                    <label>{t("struct.editor.sectiune_la", { nivel: nivelCtx?.nivel ?? "" })}</label>
                    <div className="ob-es-dim">
                      <NumarCm valoare={secAles.b_cm} laScris={(v) => redim({ b_cm: v })}
                               dezactivat={doarCitire} titlu={t("struct.editor.grosime")} />
                      <span>×</span>
                      <NumarCm valoare={secAles.L_cm} laScris={(v) => redim({ L_cm: v })}
                               dezactivat={doarCitire} titlu={t("struct.editor.lungime")} />
                      <span className="sg-muted">cm</span>
                    </div>
                    {selectorClasa(clasaAleasa, esvAles.id)}
                    <label>
                      {t("struct.editor.forma", {
                        f: forma === "compusa" ? t("struct.editor.forma_compusa") : forma ?? "I" })}
                    </label>
                    {/* brațele, câte o piesă de fiecare parte a piciorului:
                        lungimea e cât iese din el */}
                    {(piesele(esvAles, nivelEf)?.capete ?? []).filter((c) => c.brat != null).map((c) => {
                      const br = secAles.brate?.[c.brat!];
                      if (!br) return null;
                      // pe clasă: elementul ales își trage capătul ca până acum, iar
                      // membrii primesc brațul lui de după (sau îl pierd, la 0)
                      const pana = (mm: number) => {
                        const ids = tintele(esvAles.id);
                        const G1 = trageCapatul(G, esvAles.id, nivelEf, domeniuNivel, c, c.fix + c.sens * mm);
                        const dupa = G1.esv.find((x) => x.id === esvAles.id);
                        const brDupa = dupa && sectiunea(dupa, nivelEf)?.brate?.find((x) => Math.abs(x.la_mm - br.la_mm) <= 1);
                        const G2 = peMembri(G1, ids.filter((id) => id !== esvAles.id), (g, id) => {
                          const m = g.esv.find((x) => x.id === id);
                          const i = m ? indexBratului(m, nivelEf, br) : null;
                          if (i == null) return g;
                          return brDupa ? redimBrat(g, id, nivelEf, domeniuNivel, i, { L_cm: brDupa.L_cm })
                            : stergeBrat(g, id, nivelEf, domeniuNivel, i);
                        });
                        scrieSim(G, G2, ids);
                      };
                      const grosimeaBratului = (v: number) => {
                        const ids = tintele(esvAles.id);
                        scrieSim(G, peMembri(G, ids, (g, id) => {
                          const m = g.esv.find((x) => x.id === id);
                          const i = id === esvAles.id ? c.brat! : m ? indexBratului(m, nivelEf, br) : null;
                          return i == null ? g : redimBrat(g, id, nivelEf, domeniuNivel, i, { b_cm: v });
                        }), ids);
                      };
                      return (
                        <div className="ob-es-brat" key={`${c.la_mm}:${c.sens}`}>
                          <span className="sg-muted">
                            {t("struct.editor.brat_spre", {
                              dir: c.axa === 0 ? (c.sens > 0 ? "→" : "←") : (c.sens > 0 ? "↑" : "↓"),
                              m: (Math.abs(br.la_mm) / 1000).toFixed(2).replace(".", ",") })}
                          </span>
                          <div className="ob-es-dim">
                            <NumarCm valoare={br.b_cm} dezactivat={doarCitire}
                                     titlu={t("struct.editor.grosime")}
                                     laScris={grosimeaBratului} />
                            <span>×</span>
                            <NumarCm valoare={Math.round(lungimeaPiesei(c)) / 10} dezactivat={doarCitire}
                                     titlu={t("struct.editor.lungime_brat")}
                                     laScris={(v) => pana(v * 10 < BRAT_MIN_MM ? 0 : v * 10)} />
                            <span className="sg-muted">cm</span>
                            {!doarCitire && (
                              <button className="btn btng" title={t("struct.editor.sterge_brat")}
                                      onClick={() => pana(0)}>×</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <p className="ob-es-nota">{t("struct.editor.esv_lipsa_nivel")}</p>
                )}
                <label>{t("struct.editor.niveluri")}</label>
                <select className="inp" disabled={doarCitire}
                        value={nivEsvAles[nivEsvAles.length - 1]}
                        onChange={(e) => schimba(panaLaNivel(G, esvAles.id, Number(e.target.value)))}>
                  {niveluri.map((n) => (
                    <option key={n.index} value={n.index}>
                      {t("struct.editor.pana_la", { nivel: n.nivel })}
                    </option>
                  ))}
                </select>
                {iesiri.get(esvAles.id) && (() => {
                  const i = iesiri.get(esvAles.id)!;
                  return (
                    <p className="ob-es-nota ob-es-nota-iese">
                      {textulIesirii(t, esvAles.id, i)}
                    </p>
                  );
                })()}
                {typeof esvAles.baza === "object" && (
                  <p className="ob-es-nota">
                    {t("struct.editor.plantat", { grinda: esvAles.baza.grinda })}
                  </p>
                )}
                {!doarCitire && (
                  <div className="ob-es-butoane">
                    <button className="btn" onClick={roteste}>{t("struct.editor.roteste")}</button>
                    <button className="btn btnd" onClick={sterge}>
                      {domeniu === "toate" ? t("struct.editor.sterge_tot")
                        : t("struct.editor.sterge_de_aici")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {grindaAleasa && (() => {
              const s = grindaAleasa.sectiune_per_nivel[String(nivelEf)];
              const niv = nivelurileGrinzii(grindaAleasa);
              return (
                <div className="ob-es-prop">
                  <div className="ob-es-prop-cap">
                    <b>{grindaAleasa.id}</b>
                    <span className="sg-muted">{grindaAleasa.nod_i} — {grindaAleasa.nod_j}</span>
                    {!doarCitire && <Ajutor titlu={t("struct.editor.ajutor")} texte={[t("struct.editor.capat_ajutor")]} />}
                    <button className="btn btng" onClick={() => setAles(null)}>×</button>
                  </div>
                  {cipClasa(clasaAleasa)}
                  {s && (
                    <>
                      <label>{t("struct.editor.sectiune_grinda")}</label>
                      <div className="ob-es-dim">
                        <NumarCm valoare={s.b_cm} dezactivat={doarCitire}
                                 titlu={t("struct.editor.latime")}
                                 laScris={(v) => schimba(peMembri(G, tintele(grindaAleasa.id), (g, id) =>
                                   redimGrinda(g, id, domeniu === "toate" ? null : nivelEf,
                                               { b_cm: Math.max(20, Math.round(v)) })))} />
                        <span>×</span>
                        <NumarCm valoare={Math.round(s.h_m * 100)} dezactivat={doarCitire}
                                 titlu={t("struct.editor.inaltime")}
                                 laScris={(v) => schimba(peMembri(G, tintele(grindaAleasa.id), (g, id) =>
                                   redimGrinda(g, id, domeniu === "toate" ? null : nivelEf,
                                               { h_m: Math.max(20, Math.round(v)) / 100 })))} />
                        <span className="sg-muted">cm</span>
                      </div>
                      {selectorClasa(clasaAleasa, grindaAleasa.id)}
                    </>
                  )}
                  <p className="ob-es-nota">
                    {t("struct.editor.grinda_pe", {
                      niveluri: niv.map((n) => niveluri[n]?.nivel ?? n).join(", "),
                    })}
                  </p>
                  {!doarCitire && (
                    <div className="ob-es-butoane">
                      <button className="btn btnd" onClick={sterge}>
                        {domeniu === "toate" ? t("struct.editor.sterge_grinda_tot")
                          : t("struct.editor.sterge_grinda_nivel")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div></div>
        )}
        {/* dreapta-sus: de unde pornește editarea și «Salvează ca Soluția N+1» */}
        <div className="float colt-dreapta-sus ob-es-colt">
          <div className="card ob-es-actiuni">
            <div className="ob-es-titlu">
              <b>{editare.editarea && "nr" in editare.editarea
                ? numeleEditarii(editare.editarea.radacina, editare.editarea.nr)
                : t("struct.editor.titlu_nou", { n: editare.numar_nou })}</b>
              <span>{t("struct.editor.din", {
                s: editare.sursa.faza === "schita" ? t("solutie.schita")
                  : t("solutie.n", { n: editare.sursa.numar }) })}</span>
            </div>
            {(avertismente[corpIdx] ?? []).length > 0 && (
              <ul className="ob-es-avertismente">
                {(avertismente[corpIdx] ?? []).map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}
            {!doarCitire && (
              <>
                <button className="btn btnp" onClick={salveaza}
                        disabled={salvare !== "nu" || toateGreselile.length > 0
                                  || schimbate.current.size === 0}
                        title={toateGreselile.length
                          ? t("struct.editor.salveaza_blocat") : t("struct.editor.salveaza_titlu")}>
                  {salvare === "se_salveaza" ? t("struct.editor.se_salveaza")
                    : t("struct.editor.salveaza")}
                </button>
                <button className="btn" onClick={renunta}>{t("comun.renunta")}</button>
              </>
            )}
            {doarCitire && (
              <button className="btn" onClick={laInchide}>{t("struct.editor.inchide")}</button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

/** Ce spune lista despre un semnal al ochiului (`semnaleleOchiului`). */
function textulSemnalului(t: ReturnType<typeof useT>, s: "prea_mare" | "fara_reazem" | "mic",
                          o: OchiPlaca): string {
  const nr = (v: number, z = 1) => v.toFixed(z).replace(".", ",");
  if (s === "prea_mare") return t("struct.editor.semnal_prea_mare", { m: nr(o.lumina_m) });
  if (s === "fara_reazem") return t("struct.editor.semnal_fara_reazem", { pct: Math.round(o.pe_contur_pct) });
  return t("struct.editor.semnal_mic", { m2: nr(o.arie_m2) });
}

/** Nuanța fiecărui ochi: alta decât a vecinilor deja colorați (cutiile care
 *  se ating), ca două ochiuri lipite să nu se citească drept unul. */
function nuanteleOchiurilor(ochiuri: OchiVazut[]): number[] {
  const cutii = ochiuri.map((o) => cutie(o.poli[0]));
  const out: number[] = [];
  const tol = 50;
  cutii.forEach((a, i) => {
    const folosite = new Set<number>();
    for (let j = 0; j < i; j++) {
      const b = cutii[j];
      if (a[0] <= b[2] + tol && b[0] <= a[2] + tol && a[1] <= b[3] + tol && b[1] <= a[3] + tol) folosite.add(out[j]);
    }
    let c = 0;
    while (folosite.has(c) && c < NUANTE_OCHI - 1) c++;
    out.push(c);
  });
  return out;
}

/** Cota scrisă pe desen: câmpul negru, în metri, peste cifra ei. Se scrie
 *  peste valoarea întreagă din prima tastă (ca în editorul de arhitectură). */
/** Punctele în care capătul grinzii s-ar prinde de elementul de sub cursor:
 *  cercuri goale pe nod, pe mijlocul pieselor și pe capetele lor, pline pe
 *  cele de pe linia celuilalt capăt (grinda ar ieși ortogonală — recomandate);
 *  cel ales, mai mare. Fără niciunul aproape, capătul cade liber, în beton. */
function PuncteAncorare({ cadru, k, anc }: { cadru: Cadru; k: number; anc: Ancorare }) {
  const [sx, sy] = laEcran(cadru, anc.p);
  return (
    <g className="ob-es-ancore">
      {anc.puncte.map((c, i) => {
        const [x, y] = laEcran(cadru, c.p);
        return <circle key={i} cx={x} cy={y} r={3.5 / k}
                       className={`ob-es-ancora${c.fel === "linie" ? " recomandata"
                         : c.fel === "ax" ? " pe-ax" : ""}`} />;
      })}
      <circle cx={sx} cy={sy} r={5.5 / k}
              className={`ob-es-ancora aleasa${anc.snap ? "" : " libera"}`} />
    </g>
  );
}

function CotaScrisa({ k, valoare, laScris, laRenuntat }: {
  k: number; valoare: number; laScris: (v: number) => void; laRenuntat: () => void;
}) {
  const [text, setText] = useState(valoare.toFixed(2).replace(".", ","));
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.select(); }, []);
  const gata = () => {
    const v = Number(text.replace(",", "."));
    if (Number.isFinite(v) && v > 0) laScris(v); else laRenuntat();
  };
  return (
    <div className="ob-es-cota-scrisa" style={{ transform: `scale(${1 / k}) translate(-50%, -50%)` }}>
      <input ref={ref} autoFocus inputMode="decimal" value={text}
             onPointerDown={(e) => e.stopPropagation()}
             onChange={(e) => setText(e.target.value)}
             onBlur={laRenuntat}
             onKeyDown={(e) => {
               e.stopPropagation();
               if (e.key === "Enter") gata();
               else if (e.key === "Escape") laRenuntat();
             }} />
      <i>m</i>
    </div>
  );
}

/** Un număr de centimetri din panou: se scrie la Enter sau la ieșirea din
 *  câmp, nu la fiecare tastă — altfel «60» ar trece prin «6», sub latura
 *  minimă. */
function NumarCm({ valoare, laScris, dezactivat, titlu }: {
  valoare: number; laScris: (v: number) => void; dezactivat?: boolean; titlu?: string;
}) {
  const [text, setText] = useState(String(valoare));
  useEffect(() => setText(String(valoare)), [valoare]);
  const gata = () => {
    const v = Number(text.replace(",", "."));
    if (Number.isFinite(v) && v > 0 && v !== valoare) laScris(v);
    else setText(String(valoare));
  };
  return (
    <input className="inp ob-es-cm" inputMode="decimal" value={text} disabled={dezactivat}
           title={titlu} onChange={(e) => setText(e.target.value)} onBlur={gata}
           onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") gata(); }} />
  );
}

/** Semnul «i» din antetul panoului: cum se lucrează cu elementul ales, la
 *  hover (sau fixat cu un clic), ca panoul să rămână al proprietăților. */
function Ajutor({ titlu, texte }: { titlu: string; texte: string[] }) {
  if (!texte.length) return null;
  return (
    <HoverCard clasaAncora="fx-info ob-es-ajutor" latime={360} eticheta={titlu}
               continut={<>
                 <span className="fx-tip-title">{titlu}</span>
                 {texte.map((x) => <span key={x} className="fx-tip-block">{x}</span>)}
               </>}>
      <span className="fx-info-icon" aria-hidden="true">i</span>
    </HoverCard>
  );
}
